var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var _a, _b, _c;
import { BigNumber, ethers } from 'ethers';
import { sample } from 'lodash';
import { ARBITRUM, ARBITRUM_GOERLI, BASIS_POINTS_DIVISOR, CHAIN_IDS, contracts, HIGH_EXECUTION_FEES_MAP, MAX_EXCEEDING_THRESHOLD, MIN_EXCEEDING_THRESHOLD, NATIVE_TOKEN_ADDRESS, TOKENS, TRIGGER_PREFIX_ABOVE, TRIGGER_PREFIX_BELOW, USD_DECIMALS, } from './constants';
import { OrderType, TradeMode, TradeType, TriggerThresholdType, } from './types';
export function buildUrl(baseUrl, path, query) {
    var qs = query ? "?".concat(JSON.stringify(query)) : '';
    return "".concat(baseUrl).concat(path).concat(qs);
}
export var PRECISION = expandDecimals(1, 30);
export var TOKENS_MAP = {};
export var V1_TOKENS = {};
export var V2_TOKENS = {};
export var TOKENS_BY_SYMBOL_MAP = {};
export var WRAPPED_TOKENS_MAP = {};
export var NATIVE_TOKENS_MAP = {};
export var GMX_RPC_PROVIDERS = (_a = {},
    _a[ARBITRUM] = ['https://arb1.arbitrum.io/rpc'],
    _a[ARBITRUM_GOERLI] = [
        'https://goerli-rollup.arbitrum.io/rpc',
        // "https://endpoints.omniatech.io/v1/arbitrum/goerli/public",
        // "https://arbitrum-goerli.public.blastapi.io",
    ],
    _a);
export var GMX_FALLBACK_PROVIDERS = (_b = {},
    _b[ARBITRUM] = ['https://arb-mainnet.g.alchemy.com/v2/hxBqIr-vfpJ105JPYLei_ibbJLe66k46'],
    _b[ARBITRUM_GOERLI] = ['https://arb-goerli.g.alchemy.com/v2/cZfd99JyN42V9Clbs_gOvA3GSBZH1-1j'],
    _b);
export function getRpcUrl(chainId) {
    return sample(GMX_RPC_PROVIDERS[chainId]);
}
export function getFallbackRpcUrl(chainId) {
    return sample(GMX_FALLBACK_PROVIDERS[chainId]);
}
for (var j = 0; j < CHAIN_IDS.length; j++) {
    var chainId = CHAIN_IDS[j];
    TOKENS_MAP[chainId] = {};
    TOKENS_BY_SYMBOL_MAP[chainId] = {};
    V1_TOKENS[chainId] = [];
    V2_TOKENS[chainId] = [];
    var tokens = TOKENS[chainId];
    var wrappedTokenAddress = void 0;
    for (var i = 0; i < tokens.length; i++) {
        var token = tokens[i];
        TOKENS_MAP[chainId][token.value] = token;
        TOKENS_BY_SYMBOL_MAP[chainId][token.symbol] = token;
        if (token.isWrapped) {
            WRAPPED_TOKENS_MAP[chainId] = token;
            wrappedTokenAddress = token.value;
        }
        if (token.isNative) {
            NATIVE_TOKENS_MAP[chainId] = token;
        }
        if (token.isV1Available && !token.isTempHidden) {
            V1_TOKENS[chainId].push(token);
        }
        if (!token.isPlatformToken && !token.isTempHidden) {
            V2_TOKENS[chainId].push(token);
        }
    }
    NATIVE_TOKENS_MAP[chainId].wrappedAddress = wrappedTokenAddress;
}
export var DUST_USD = expandDecimals(1, USD_DECIMALS);
export var getContract = function (_a) {
    var chainId = _a.chainId, name = _a.name;
    if (chainId === 1)
        return;
    if (chainId === 5)
        return;
    var chainIdNumber = Number(chainId);
    if (!contracts[chainIdNumber])
        return;
    return contracts[chainIdNumber][name];
};
export function getToken(chainId, address) {
    if (!TOKENS_MAP[chainId]) {
        throw new Error("Incorrect chainId ".concat(chainId));
    }
    if (!TOKENS_MAP[chainId][address]) {
        throw new Error("Incorrect address \"".concat(address, "\" for chainId ").concat(chainId));
    }
    return TOKENS_MAP[chainId][address];
}
export function getWrappedToken(chainId) {
    return WRAPPED_TOKENS_MAP[chainId];
}
export function convertTokenAddress(chainId, address, convertTo) {
    var wrappedToken = getWrappedToken(chainId);
    if (convertTo === 'wrapped' && address === NATIVE_TOKEN_ADDRESS) {
        return wrappedToken.value;
    }
    if (convertTo === 'native' && address === wrappedToken.value) {
        return NATIVE_TOKEN_ADDRESS;
    }
    return address;
}
export function getMarketFullName(p) {
    var indexToken = p.indexToken, longToken = p.longToken, shortToken = p.shortToken, isSpotOnly = p.isSpotOnly;
    return "".concat(getMarketIndexName({ indexToken: indexToken, isSpotOnly: isSpotOnly }), " [").concat(getMarketPoolName({ longToken: longToken, shortToken: shortToken }), "]");
}
export function getMarketIndexName(p) {
    var indexToken = p.indexToken, isSpotOnly = p.isSpotOnly;
    if (isSpotOnly) {
        return "SWAP-ONLY";
    }
    return "".concat(indexToken.baseSymbol || indexToken.symbol, "/USD");
}
export function getMarketPoolName(p) {
    var longToken = p.longToken, shortToken = p.shortToken;
    if (longToken.value === shortToken.value) {
        return longToken.symbol;
    }
    return "".concat(longToken.symbol, "-").concat(shortToken.symbol);
}
export function hashData(dataTypes, dataValues) {
    var bytes = ethers.utils.defaultAbiCoder.encode(dataTypes, dataValues);
    var hash = ethers.utils.keccak256(ethers.utils.arrayify(bytes));
    return hash;
}
export function hashString(string) {
    return hashData(['string'], [string]);
}
export var MIN_COLLATERAL_USD_KEY = hashString('MIN_COLLATERAL_USD');
export var MIN_POSITION_SIZE_USD_KEY = hashString('MIN_POSITION_SIZE_USD');
export function getByKey(obj, key) {
    if (!obj || !key)
        return undefined;
    if (obj[key])
        return obj[key];
    return undefined;
}
export function getTokenByKey(chainId, obj, key) {
    if (!obj || !key)
        return undefined;
    if (obj[key])
        return obj[key];
    return getToken(chainId, key);
}
export function getNativeToken(chainId) {
    return NATIVE_TOKENS_MAP[chainId];
}
export function getTokens(chainId) {
    return TOKENS[chainId];
}
export function getV1Tokens(chainId) {
    return V1_TOKENS[chainId];
}
export function getV2Tokens(chainId) {
    return V2_TOKENS[chainId];
}
export function getTokensMap(chainId) {
    return TOKENS_MAP[chainId];
}
export function getWhitelistedV1Tokens(chainId) {
    return getV1Tokens(chainId);
}
export function getVisibleV1Tokens(chainId) {
    return getV1Tokens(chainId).filter(function (token) { return !token.isWrapped; });
}
export function isValidToken(chainId, address) {
    if (!TOKENS_MAP[chainId]) {
        throw new Error("Incorrect chainId ".concat(chainId));
    }
    return address in TOKENS_MAP[chainId];
}
export function getTokenBySymbol(chainId, symbol) {
    var token = TOKENS_BY_SYMBOL_MAP[chainId][symbol];
    if (!token) {
        throw new Error("Incorrect symbol \"".concat(symbol, "\" for chainId ").concat(chainId));
    }
    return token;
}
export function getNormalizedTokenSymbol(tokenSymbol) {
    if (['WBTC', 'WETH', 'WAVAX'].includes(tokenSymbol)) {
        return tokenSymbol.substr(1);
    }
    else if (tokenSymbol === 'BTC.b') {
        return 'BTC';
    }
    return tokenSymbol;
}
export function isChartAvailabeForToken(chainId, tokenSymbol) {
    var token;
    try {
        token = getTokenBySymbol(chainId, tokenSymbol);
    }
    catch (e) {
        return false;
    }
    if (token.isChartDisabled || token.isPlatformToken)
        return false;
    return true;
}
export function getPriceDecimals(chainId, tokenSymbol) {
    var _a;
    if (!tokenSymbol)
        return 2;
    try {
        var token = getTokenBySymbol(chainId, tokenSymbol);
        return (_a = token.priceDecimals) !== null && _a !== void 0 ? _a : 2;
    }
    catch (e) {
        return 2;
    }
}
var ORACLE_KEEPER_URLS = (_c = {},
    _c[ARBITRUM] = ['https://arbitrum.gmx-oracle.io', 'https://arbitrum-2.gmx-oracle.io'],
    _c[ARBITRUM_GOERLI] = ['https://oracle-api-arb-goerli-xyguy.ondigitalocean.app'],
    _c);
export function getOracleKeeperUrl(chainId, index) {
    var urls = ORACLE_KEEPER_URLS[chainId];
    return urls[index || 0];
}
export function fetchTickers(chainId, index) {
    return fetch(buildUrl(getOracleKeeperUrl(chainId, index), '/prices/tickers'))
        .then(function (res) { return res.json(); })
        .then(function (res) {
        if (!res.length) {
            throw new Error('Invalid tickers response');
        }
        return res;
    })
        .catch(function (e) {
        if (chainId === ARBITRUM_GOERLI) {
            console.error(e);
            return;
        }
        if (index && index > 0) {
            console.error(e);
            return;
        }
        return fetchTickers(chainId, 1);
    });
}
export function bigNumberify(n) {
    try {
        return BigNumber.from(n);
    }
    catch (e) {
        // eslint-disable-next-line no-console
        console.error('bigNumberify error', e);
        return undefined;
    }
}
export function expandDecimals(n, decimals) {
    // @ts-ignore
    return bigNumberify(n).mul(bigNumberify(10).pow(decimals));
}
export function parseOraclePrice(price, tokenDecimals, oracleDecimals) {
    return expandDecimals(price, tokenDecimals + oracleDecimals);
}
export function convertToContractPrice(price, tokenDecimals) {
    return price.div(expandDecimals(1, tokenDecimals));
}
export function convertToContractTokenPrices(prices, tokenDecimals) {
    return {
        min: convertToContractPrice(prices.minPrice, tokenDecimals),
        max: convertToContractPrice(prices.maxPrice, tokenDecimals),
    };
}
export function getContractMarketPrices(tokensData, market) {
    var indexToken = getByKey(tokensData, market.indexTokenAddress);
    var longToken = getByKey(tokensData, market.longTokenAddress);
    var shortToken = getByKey(tokensData, market.shortTokenAddress);
    if (!indexToken || !longToken || !shortToken) {
        return undefined;
    }
    return {
        indexTokenPrice: convertToContractTokenPrices(indexToken.prices, indexToken.decimals),
        longTokenPrice: convertToContractTokenPrices(longToken.prices, longToken.decimals),
        shortTokenPrice: convertToContractTokenPrices(shortToken.prices, shortToken.decimals),
    };
}
export function getMarketsGraph(markets) {
    var e_1, _a;
    var graph = {
        abjacencyList: {},
        edges: [],
    };
    try {
        for (var markets_1 = __values(markets), markets_1_1 = markets_1.next(); !markets_1_1.done; markets_1_1 = markets_1.next()) {
            var market = markets_1_1.value;
            var longTokenAddress = market.longTokenAddress, shortTokenAddress = market.shortTokenAddress, marketTokenAddress = market.marketTokenAddress, isSameCollaterals = market.isSameCollaterals, isDisabled = market.isDisabled;
            if (isSameCollaterals || isDisabled) {
                continue;
            }
            var longShortEdge = {
                marketInfo: market,
                marketAddress: marketTokenAddress,
                from: longTokenAddress,
                to: shortTokenAddress,
            };
            var shortLongEdge = {
                marketInfo: market,
                marketAddress: marketTokenAddress,
                from: shortTokenAddress,
                to: longTokenAddress,
            };
            graph.abjacencyList[longTokenAddress] = graph.abjacencyList[longTokenAddress] || [];
            graph.abjacencyList[longTokenAddress].push(longShortEdge);
            graph.abjacencyList[shortTokenAddress] = graph.abjacencyList[shortTokenAddress] || [];
            graph.abjacencyList[shortTokenAddress].push(shortLongEdge);
            graph.edges.push(longShortEdge, shortLongEdge);
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (markets_1_1 && !markets_1_1.done && (_a = markets_1.return)) _a.call(markets_1);
        }
        finally { if (e_1) throw e_1.error; }
    }
    return graph;
}
export function getTokenPoolType(marketInfo, tokenAddress) {
    var longToken = marketInfo.longToken, shortToken = marketInfo.shortToken;
    if (tokenAddress === longToken.value || (tokenAddress === NATIVE_TOKEN_ADDRESS && longToken.isWrapped)) {
        return 'long';
    }
    if (tokenAddress === shortToken.value || (tokenAddress === NATIVE_TOKEN_ADDRESS && shortToken.isWrapped)) {
        return 'short';
    }
    return undefined;
}
export function convertToTokenAmount(usd, tokenDecimals, price) {
    if (!usd || typeof tokenDecimals !== 'number' || !(price === null || price === void 0 ? void 0 : price.gt(0))) {
        return undefined;
    }
    return usd.mul(expandDecimals(1, tokenDecimals)).div(price);
}
export function getMidPrice(prices) {
    return prices.minPrice.add(prices.maxPrice).div(2);
}
export function convertToUsd(tokenAmount, tokenDecimals, price) {
    if (!tokenAmount || typeof tokenDecimals !== 'number' || !price) {
        return undefined;
    }
    return tokenAmount.mul(price).div(expandDecimals(1, tokenDecimals));
}
export function getNextPoolAmountsParams(p) {
    var marketInfo = p.marketInfo, longToken = p.longToken, shortToken = p.shortToken, longPoolAmount = p.longPoolAmount, shortPoolAmount = p.shortPoolAmount, longDeltaUsd = p.longDeltaUsd, shortDeltaUsd = p.shortDeltaUsd;
    var longPrice = getMidPrice(longToken.prices);
    var shortPrice = getMidPrice(shortToken.prices);
    var longPoolUsd = convertToUsd(longPoolAmount, longToken.decimals, longPrice);
    var shortPoolUsd = convertToUsd(shortPoolAmount, shortToken.decimals, shortPrice);
    var longPoolUsdAdjustment = convertToUsd(marketInfo.longPoolAmountAdjustment, longToken.decimals, longPrice);
    var shortPoolUsdAdjustment = convertToUsd(marketInfo.shortPoolAmountAdjustment, shortToken.decimals, shortPrice);
    var nextLongPoolUsd = longPoolUsd.add(longDeltaUsd).add(longPoolUsdAdjustment);
    var nextShortPoolUsd = shortPoolUsd.add(shortDeltaUsd).add(shortPoolUsdAdjustment);
    return {
        longPoolUsd: longPoolUsd,
        shortPoolUsd: shortPoolUsd,
        nextLongPoolUsd: nextLongPoolUsd,
        nextShortPoolUsd: nextShortPoolUsd,
    };
}
export function applyImpactFactor(diff, factor, exponent) {
    // Convert diff and exponent to float js numbers
    var _diff = Number(diff) / Math.pow(10, 30);
    var _exponent = Number(exponent) / Math.pow(10, 30);
    // Pow and convert back to BigNumber with 30 decimals
    var result = bigNumberify(BigInt(Math.round(Math.pow(_diff, _exponent) * Math.pow(10, 30))));
    result = result.mul(factor).div(expandDecimals(1, 30));
    return result;
}
export function calculateImpactForSameSideRebalance(p) {
    var currentDiff = p.currentDiff, nextDiff = p.nextDiff, hasPositiveImpact = p.hasPositiveImpact, factor = p.factor, exponentFactor = p.exponentFactor;
    var currentImpact = applyImpactFactor(currentDiff, factor, exponentFactor);
    var nextImpact = applyImpactFactor(nextDiff, factor, exponentFactor);
    var deltaDiff = currentImpact.sub(nextImpact).abs();
    return hasPositiveImpact ? deltaDiff : BigNumber.from(0).sub(deltaDiff);
}
export function calculateImpactForCrossoverRebalance(p) {
    var currentDiff = p.currentDiff, nextDiff = p.nextDiff, factorNegative = p.factorNegative, factorPositive = p.factorPositive, exponentFactor = p.exponentFactor;
    var positiveImpact = applyImpactFactor(currentDiff, factorPositive, exponentFactor);
    var negativeImpactUsd = applyImpactFactor(nextDiff, factorNegative, exponentFactor);
    var deltaDiffUsd = positiveImpact.sub(negativeImpactUsd).abs();
    return positiveImpact.gt(negativeImpactUsd) ? deltaDiffUsd : BigNumber.from(0).sub(deltaDiffUsd);
}
export function getPriceImpactUsd(p) {
    var nextLongUsd = p.nextLongUsd, nextShortUsd = p.nextShortUsd;
    if (nextLongUsd.lt(0) || nextShortUsd.lt(0)) {
        if (p.fallbackToZero) {
            return BigNumber.from(0);
        }
        else {
            throw new Error('Negative pool amount');
        }
    }
    var currentDiff = p.currentLongUsd.sub(p.currentShortUsd).abs();
    var nextDiff = nextLongUsd.sub(nextShortUsd).abs();
    var isSameSideRebalance = p.currentLongUsd.lt(p.currentShortUsd) === nextLongUsd.lt(nextShortUsd);
    var impactUsd;
    if (isSameSideRebalance) {
        var hasPositiveImpact = nextDiff.lt(currentDiff);
        var factor = hasPositiveImpact ? p.factorPositive : p.factorNegative;
        impactUsd = calculateImpactForSameSideRebalance({
            currentDiff: currentDiff,
            nextDiff: nextDiff,
            hasPositiveImpact: hasPositiveImpact,
            factor: factor,
            exponentFactor: p.exponentFactor,
        });
    }
    else {
        impactUsd = calculateImpactForCrossoverRebalance({
            currentDiff: currentDiff,
            nextDiff: nextDiff,
            factorPositive: p.factorPositive,
            factorNegative: p.factorNegative,
            exponentFactor: p.exponentFactor,
        });
    }
    return impactUsd;
}
export function getPriceImpactForSwap(marketInfo, tokenA, tokenB, usdDeltaTokenA, usdDeltaTokenB, opts) {
    if (opts === void 0) { opts = {}; }
    var tokenAPoolType = getTokenPoolType(marketInfo, tokenA.value);
    var tokenBPoolType = getTokenPoolType(marketInfo, tokenB.value);
    if (tokenAPoolType === undefined ||
        tokenBPoolType === undefined ||
        (tokenAPoolType === tokenBPoolType && !marketInfo.isSameCollaterals)) {
        throw new Error("Invalid tokens to swap ".concat(marketInfo.marketTokenAddress, " ").concat(tokenA.value, " ").concat(tokenB.value));
    }
    var _a = __read(tokenAPoolType === 'long' ? [tokenA, tokenB] : [tokenB, tokenA], 2), longToken = _a[0], shortToken = _a[1];
    var _b = __read(tokenAPoolType === 'long' ? [usdDeltaTokenA, usdDeltaTokenB] : [usdDeltaTokenB, usdDeltaTokenA], 2), longDeltaUsd = _b[0], shortDeltaUsd = _b[1];
    var _c = getNextPoolAmountsParams({
        marketInfo: marketInfo,
        longToken: longToken,
        shortToken: shortToken,
        longPoolAmount: marketInfo.longPoolAmount,
        shortPoolAmount: marketInfo.shortPoolAmount,
        longDeltaUsd: longDeltaUsd,
        shortDeltaUsd: shortDeltaUsd,
    }), longPoolUsd = _c.longPoolUsd, shortPoolUsd = _c.shortPoolUsd, nextLongPoolUsd = _c.nextLongPoolUsd, nextShortPoolUsd = _c.nextShortPoolUsd;
    var priceImpactUsd = getPriceImpactUsd({
        currentLongUsd: longPoolUsd,
        currentShortUsd: shortPoolUsd,
        nextLongUsd: nextLongPoolUsd,
        nextShortUsd: nextShortPoolUsd,
        factorPositive: marketInfo.swapImpactFactorPositive,
        factorNegative: marketInfo.swapImpactFactorNegative,
        exponentFactor: marketInfo.swapImpactExponentFactor,
        fallbackToZero: opts.fallbackToZero,
    });
    if (priceImpactUsd.gt(0)) {
        return priceImpactUsd;
    }
    var virtualInventoryLong = marketInfo.virtualPoolAmountForLongToken;
    var virtualInventoryShort = marketInfo.virtualPoolAmountForShortToken;
    if (!virtualInventoryLong.gt(0) || !virtualInventoryShort.gt(0)) {
        return priceImpactUsd;
    }
    var virtualInventoryParams = getNextPoolAmountsParams({
        marketInfo: marketInfo,
        longToken: longToken,
        shortToken: shortToken,
        longPoolAmount: virtualInventoryLong,
        shortPoolAmount: virtualInventoryShort,
        longDeltaUsd: longDeltaUsd,
        shortDeltaUsd: shortDeltaUsd,
    });
    var priceImpactUsdForVirtualInventory = getPriceImpactUsd({
        currentLongUsd: virtualInventoryParams.longPoolUsd,
        currentShortUsd: virtualInventoryParams.shortPoolUsd,
        nextLongUsd: virtualInventoryParams.nextLongPoolUsd,
        nextShortUsd: virtualInventoryParams.nextShortPoolUsd,
        factorPositive: marketInfo.swapImpactFactorPositive,
        factorNegative: marketInfo.swapImpactFactorNegative,
        exponentFactor: marketInfo.swapImpactExponentFactor,
        fallbackToZero: opts.fallbackToZero,
    });
    return priceImpactUsdForVirtualInventory.lt(priceImpactUsd) ? priceImpactUsdForVirtualInventory : priceImpactUsd;
}
export function applyFactor(value, factor) {
    return value.mul(factor).div(PRECISION);
}
export function roundUpMagnitudeDivision(a, b) {
    if (a.lt(0)) {
        return a.sub(b).add(1).div(b);
    }
    return a.add(b).sub(1).div(b);
}
export function getSwapFee(marketInfo, swapAmount, forPositiveImpact) {
    var factor = forPositiveImpact
        ? marketInfo.swapFeeFactorForPositiveImpact
        : marketInfo.swapFeeFactorForNegativeImpact;
    return applyFactor(swapAmount, factor);
}
export function applySwapImpactWithCap(marketInfo, token, priceImpactDeltaUsd) {
    var tokenPoolType = getTokenPoolType(marketInfo, token.value);
    if (!tokenPoolType) {
        throw new Error("Token ".concat(token.value, " is not a collateral of the market ").concat(marketInfo.marketTokenAddress));
    }
    var isLongCollateral = tokenPoolType === 'long';
    var price = priceImpactDeltaUsd.gt(0) ? token.prices.maxPrice : token.prices.minPrice;
    var impactDeltaAmount;
    if (priceImpactDeltaUsd.gt(0)) {
        // round positive impactAmount down, this will be deducted from the swap impact pool for the user
        impactDeltaAmount = convertToTokenAmount(priceImpactDeltaUsd, token.decimals, price);
        var maxImpactAmount = isLongCollateral
            ? marketInfo.swapImpactPoolAmountLong
            : marketInfo.swapImpactPoolAmountShort;
        if (impactDeltaAmount.gt(maxImpactAmount)) {
            impactDeltaAmount = maxImpactAmount;
        }
    }
    else {
        // round negative impactAmount up, this will be deducted from the user
        impactDeltaAmount = roundUpMagnitudeDivision(priceImpactDeltaUsd.mul(expandDecimals(1, token.decimals)), price);
    }
    return impactDeltaAmount;
}
export function getPoolUsdWithoutPnl(marketInfo, isLong, priceType) {
    var _a, _b;
    var poolAmount = isLong ? marketInfo.longPoolAmount : marketInfo.shortPoolAmount;
    var token = isLong ? marketInfo.longToken : marketInfo.shortToken;
    var price;
    if (priceType === 'minPrice') {
        price = (_a = token.prices) === null || _a === void 0 ? void 0 : _a.minPrice;
    }
    else if (priceType === 'maxPrice') {
        price = (_b = token.prices) === null || _b === void 0 ? void 0 : _b.maxPrice;
    }
    else {
        price = getMidPrice(token.prices);
    }
    return convertToUsd(poolAmount, token.decimals, price);
}
export function getReservedUsd(marketInfo, isLong) {
    var indexToken = marketInfo.indexToken;
    if (isLong) {
        return convertToUsd(marketInfo.longInterestInTokens, marketInfo.indexToken.decimals, indexToken.prices.maxPrice);
    }
    else {
        return marketInfo.shortInterestUsd;
    }
}
export function getAvailableUsdLiquidityForCollateral(marketInfo, isLong) {
    var poolUsd = getPoolUsdWithoutPnl(marketInfo, isLong, 'minPrice');
    if (marketInfo.isSpotOnly) {
        return poolUsd;
    }
    var reservedUsd = getReservedUsd(marketInfo, isLong);
    var maxReserveFactor = isLong ? marketInfo.reserveFactorLong : marketInfo.reserveFactorShort;
    if (maxReserveFactor.eq(0)) {
        return BigNumber.from(0);
    }
    var minPoolUsd = reservedUsd.mul(PRECISION).div(maxReserveFactor);
    var liqudiity = poolUsd.sub(minPoolUsd);
    return liqudiity;
}
export function getSwapStats(p) {
    var marketInfo = p.marketInfo, tokenInAddress = p.tokenInAddress, tokenOutAddress = p.tokenOutAddress, usdIn = p.usdIn, shouldApplyPriceImpact = p.shouldApplyPriceImpact;
    var isWrap = tokenInAddress === NATIVE_TOKEN_ADDRESS;
    var isUnwrap = tokenOutAddress === NATIVE_TOKEN_ADDRESS;
    var tokenIn = getTokenPoolType(marketInfo, tokenInAddress) === 'long' ? marketInfo.longToken : marketInfo.shortToken;
    var tokenOut = getTokenPoolType(marketInfo, tokenOutAddress) === 'long' ? marketInfo.longToken : marketInfo.shortToken;
    var priceIn = tokenIn.prices.minPrice;
    var priceOut = tokenOut.prices.maxPrice;
    var amountIn = convertToTokenAmount(usdIn, tokenIn.decimals, priceIn);
    var priceImpactDeltaUsd;
    try {
        priceImpactDeltaUsd = getPriceImpactForSwap(marketInfo, tokenIn, tokenOut, usdIn, usdIn.mul(-1));
    }
    catch (e) {
        return {
            swapFeeUsd: BigNumber.from(0),
            swapFeeAmount: BigNumber.from(0),
            isWrap: isWrap,
            isUnwrap: isUnwrap,
            marketAddress: marketInfo.marketTokenAddress,
            tokenInAddress: tokenInAddress,
            tokenOutAddress: tokenOutAddress,
            priceImpactDeltaUsd: BigNumber.from(0),
            amountIn: amountIn,
            amountInAfterFees: amountIn,
            usdIn: usdIn,
            amountOut: BigNumber.from(0),
            usdOut: BigNumber.from(0),
            isOutLiquidity: true,
        };
    }
    var swapFeeAmount = getSwapFee(marketInfo, amountIn, priceImpactDeltaUsd.gt(0));
    var swapFeeUsd = getSwapFee(marketInfo, usdIn, priceImpactDeltaUsd.gt(0));
    var amountInAfterFees = amountIn.sub(swapFeeAmount);
    var usdInAfterFees = usdIn.sub(swapFeeUsd);
    var usdOut = usdInAfterFees;
    var amountOut = convertToTokenAmount(usdOut, tokenOut.decimals, priceOut);
    var cappedImpactDeltaUsd;
    if (priceImpactDeltaUsd.gt(0)) {
        var positiveImpactAmount = applySwapImpactWithCap(marketInfo, tokenOut, priceImpactDeltaUsd);
        cappedImpactDeltaUsd = convertToUsd(positiveImpactAmount, tokenOut.decimals, priceOut);
    }
    else {
        var negativeImpactAmount = applySwapImpactWithCap(marketInfo, tokenIn, priceImpactDeltaUsd);
        cappedImpactDeltaUsd = convertToUsd(negativeImpactAmount, tokenIn.decimals, priceIn);
    }
    if (shouldApplyPriceImpact) {
        usdOut = usdOut.add(cappedImpactDeltaUsd);
    }
    if (usdOut.lt(0)) {
        usdOut = BigNumber.from(0);
    }
    amountOut = convertToTokenAmount(usdOut, tokenOut.decimals, priceOut);
    var liquidity = getAvailableUsdLiquidityForCollateral(marketInfo, getTokenPoolType(marketInfo, tokenOutAddress) === 'long');
    var isOutLiquidity = liquidity.lt(usdOut);
    return {
        swapFeeUsd: swapFeeUsd,
        swapFeeAmount: swapFeeAmount,
        isWrap: isWrap,
        isUnwrap: isUnwrap,
        marketAddress: marketInfo.marketTokenAddress,
        tokenInAddress: tokenInAddress,
        tokenOutAddress: tokenOutAddress,
        priceImpactDeltaUsd: cappedImpactDeltaUsd,
        amountIn: amountIn,
        amountInAfterFees: amountInAfterFees,
        usdIn: usdIn,
        amountOut: amountOut,
        usdOut: usdOut,
        isOutLiquidity: isOutLiquidity,
    };
}
export var createSwapEstimator = function (marketsInfoData) {
    return function (e, usdIn) {
        var marketInfo = marketsInfoData[e.marketAddress];
        var swapStats = getSwapStats({
            marketInfo: marketInfo,
            usdIn: usdIn,
            tokenInAddress: e.from,
            tokenOutAddress: e.to,
            shouldApplyPriceImpact: true,
        });
        var isOutLiquidity = swapStats === null || swapStats === void 0 ? void 0 : swapStats.isOutLiquidity;
        var usdOut = swapStats === null || swapStats === void 0 ? void 0 : swapStats.usdOut;
        if (!usdOut || isOutLiquidity) {
            return {
                usdOut: BigNumber.from(0),
            };
        }
        return {
            usdOut: usdOut,
        };
    };
};
export function getOppositeCollateral(marketInfo, tokenAddress) {
    var poolType = getTokenPoolType(marketInfo, tokenAddress);
    if (poolType === 'long') {
        return marketInfo.shortToken;
    }
    if (poolType === 'short') {
        return marketInfo.longToken;
    }
    return undefined;
}
export function getMaxSwapPathLiquidity(p) {
    var e_2, _a;
    var marketsInfoData = p.marketsInfoData, swapPath = p.swapPath, initialCollateralAddress = p.initialCollateralAddress;
    if (swapPath.length === 0) {
        return BigNumber.from(0);
    }
    var minMarketLiquidity = ethers.constants.MaxUint256;
    var tokenInAddress = initialCollateralAddress;
    try {
        for (var swapPath_1 = __values(swapPath), swapPath_1_1 = swapPath_1.next(); !swapPath_1_1.done; swapPath_1_1 = swapPath_1.next()) {
            var marketAddress = swapPath_1_1.value;
            var marketInfo = getByKey(marketsInfoData, marketAddress);
            if (!marketInfo) {
                return BigNumber.from(0);
            }
            var tokenOut = getOppositeCollateral(marketInfo, tokenInAddress);
            if (!tokenOut) {
                return BigNumber.from(0);
            }
            var isTokenOutLong = getTokenPoolType(marketInfo, tokenOut.value) === 'long';
            var liquidity = getAvailableUsdLiquidityForCollateral(marketInfo, isTokenOutLong);
            if (liquidity.lt(minMarketLiquidity)) {
                minMarketLiquidity = liquidity;
            }
            tokenInAddress = tokenOut.value;
        }
    }
    catch (e_2_1) { e_2 = { error: e_2_1 }; }
    finally {
        try {
            if (swapPath_1_1 && !swapPath_1_1.done && (_a = swapPath_1.return)) _a.call(swapPath_1);
        }
        finally { if (e_2) throw e_2.error; }
    }
    if (minMarketLiquidity.eq(ethers.constants.MaxUint256)) {
        return BigNumber.from(0);
    }
    return minMarketLiquidity;
}
export function findAllPaths(marketsInfoData, graph, from, to, maxDepth) {
    var e_3, _a;
    if (maxDepth === void 0) { maxDepth = 3; }
    var routes = [];
    var edges = graph.abjacencyList[from];
    if (!(edges === null || edges === void 0 ? void 0 : edges.length)) {
        return undefined;
    }
    try {
        for (var edges_1 = __values(edges), edges_1_1 = edges_1.next(); !edges_1_1.done; edges_1_1 = edges_1.next()) {
            var e = edges_1_1.value;
            dfs(e, [], [], {});
        }
    }
    catch (e_3_1) { e_3 = { error: e_3_1 }; }
    finally {
        try {
            if (edges_1_1 && !edges_1_1.done && (_a = edges_1.return)) _a.call(edges_1);
        }
        finally { if (e_3) throw e_3.error; }
    }
    function dfs(edge, path, pathEdges, visited) {
        var e_4, _a;
        // avoid too deep paths and cycles
        if (path.length >= maxDepth || visited[edge.marketAddress]) {
            return;
        }
        visited[edge.marketAddress] = true;
        pathEdges.push(edge);
        path.push(edge.marketAddress);
        if (edge.to === to) {
            routes.push({
                edged: pathEdges,
                path: path,
                liquidity: getMaxSwapPathLiquidity({ marketsInfoData: marketsInfoData, swapPath: path, initialCollateralAddress: from }),
            });
            return;
        }
        var edges = graph.abjacencyList[edge.to];
        if (!(edges === null || edges === void 0 ? void 0 : edges.length)) {
            return;
        }
        try {
            for (var edges_2 = __values(edges), edges_2_1 = edges_2.next(); !edges_2_1.done; edges_2_1 = edges_2.next()) {
                var e = edges_2_1.value;
                dfs(e, __spreadArray([], __read(path), false), __spreadArray([], __read(pathEdges), false), __assign({}, visited));
            }
        }
        catch (e_4_1) { e_4 = { error: e_4_1 }; }
        finally {
            try {
                if (edges_2_1 && !edges_2_1.done && (_a = edges_2.return)) _a.call(edges_2);
            }
            finally { if (e_4) throw e_4.error; }
        }
    }
    return routes;
}
export function getBestSwapPath(routes, usdIn, estimator) {
    var e_5, _a;
    if (routes.length === 0) {
        return undefined;
    }
    var bestPath = routes[0].path;
    var bestUsdOut = BigNumber.from(0);
    try {
        for (var routes_1 = __values(routes), routes_1_1 = routes_1.next(); !routes_1_1.done; routes_1_1 = routes_1.next()) {
            var route = routes_1_1.value;
            try {
                var pathUsdOut = route.edged.reduce(function (prevUsdOut, edge) {
                    var usdOut = estimator(edge, prevUsdOut).usdOut;
                    return usdOut;
                }, usdIn);
                if (pathUsdOut.gt(bestUsdOut)) {
                    bestPath = route.path;
                    bestUsdOut = pathUsdOut;
                }
            }
            catch (e) {
                continue;
            }
        }
    }
    catch (e_5_1) { e_5 = { error: e_5_1 }; }
    finally {
        try {
            if (routes_1_1 && !routes_1_1.done && (_a = routes_1.return)) _a.call(routes_1);
        }
        finally { if (e_5) throw e_5.error; }
    }
    return bestPath;
}
export function getSwapPathStats(p) {
    var marketsInfoData = p.marketsInfoData, swapPath = p.swapPath, initialCollateralAddress = p.initialCollateralAddress, usdIn = p.usdIn, shouldUnwrapNativeToken = p.shouldUnwrapNativeToken, shouldApplyPriceImpact = p.shouldApplyPriceImpact, wrappedNativeTokenAddress = p.wrappedNativeTokenAddress;
    if (swapPath.length === 0) {
        return undefined;
    }
    var swapSteps = [];
    var usdOut = usdIn;
    var tokenInAddress = initialCollateralAddress;
    var tokenOutAddress = initialCollateralAddress;
    var totalSwapPriceImpactDeltaUsd = BigNumber.from(0);
    var totalSwapFeeUsd = BigNumber.from(0);
    for (var i = 0; i < swapPath.length; i++) {
        var marketAddress = swapPath[i];
        var marketInfo = marketsInfoData[marketAddress];
        tokenOutAddress = getOppositeCollateral(marketInfo, tokenInAddress).value;
        if (i === swapPath.length - 1 && shouldUnwrapNativeToken && tokenOutAddress === wrappedNativeTokenAddress) {
            tokenOutAddress = NATIVE_TOKEN_ADDRESS;
        }
        var swapStep = getSwapStats({
            marketInfo: marketInfo,
            tokenInAddress: tokenInAddress,
            tokenOutAddress: tokenOutAddress,
            usdIn: usdOut,
            shouldApplyPriceImpact: shouldApplyPriceImpact,
        });
        tokenInAddress = swapStep.tokenOutAddress;
        usdOut = swapStep.usdOut;
        totalSwapPriceImpactDeltaUsd = totalSwapPriceImpactDeltaUsd.add(swapStep.priceImpactDeltaUsd);
        totalSwapFeeUsd = totalSwapFeeUsd.add(swapStep.swapFeeUsd);
        swapSteps.push(swapStep);
    }
    var lastStep = swapSteps[swapSteps.length - 1];
    var targetMarketAddress = lastStep.marketAddress;
    var amountOut = lastStep.amountOut;
    var totalFeesDeltaUsd = BigNumber.from(0).sub(totalSwapFeeUsd).add(totalSwapPriceImpactDeltaUsd);
    return {
        swapPath: swapPath,
        tokenInAddress: tokenInAddress,
        tokenOutAddress: tokenOutAddress,
        targetMarketAddress: targetMarketAddress,
        swapSteps: swapSteps,
        usdOut: usdOut,
        amountOut: amountOut,
        totalSwapFeeUsd: totalSwapFeeUsd,
        totalSwapPriceImpactDeltaUsd: totalSwapPriceImpactDeltaUsd,
        totalFeesDeltaUsd: totalFeesDeltaUsd,
    };
}
export function getTriggerThresholdType(orderType, isLong) {
    // limit order
    if (orderType === OrderType.LimitIncrease) {
        return isLong ? TriggerThresholdType.Below : TriggerThresholdType.Above;
    }
    // take profit order
    if (orderType === OrderType.LimitDecrease) {
        return isLong ? TriggerThresholdType.Above : TriggerThresholdType.Below;
    }
    // stop loss order
    if (orderType === OrderType.StopLossDecrease) {
        return isLong ? TriggerThresholdType.Below : TriggerThresholdType.Above;
    }
    throw new Error('Invalid trigger order type');
}
export function getIsEquivalentTokens(token1, token2) {
    if (token1.value === token2.value) {
        return true;
    }
    if (token1.wrappedAddress === token2.value || token2.wrappedAddress === token1.value) {
        return true;
    }
    return false;
}
export function getShouldUseMaxPrice(isIncrease, isLong) {
    return isIncrease ? isLong : !isLong;
}
export function getMarkPrice(p) {
    var prices = p.prices, isIncrease = p.isIncrease, isLong = p.isLong;
    var shouldUseMaxPrice = getShouldUseMaxPrice(isIncrease, isLong);
    return shouldUseMaxPrice ? prices.maxPrice : prices.minPrice;
}
export function adjustForDecimals(amount, divDecimals, mulDecimals) {
    return amount.mul(expandDecimals(1, mulDecimals)).div(expandDecimals(1, divDecimals));
}
export function getAmountByRatio(p) {
    var fromToken = p.fromToken, toToken = p.toToken, fromTokenAmount = p.fromTokenAmount, ratio = p.ratio, shouldInvertRatio = p.shouldInvertRatio;
    if (getIsEquivalentTokens(fromToken, toToken) || fromTokenAmount.eq(0)) {
        return p.fromTokenAmount;
    }
    var _ratio = shouldInvertRatio ? PRECISION.mul(PRECISION).div(ratio) : ratio;
    var adjustedDecimalsRatio = adjustForDecimals(_ratio, fromToken.decimals, toToken.decimals);
    return p.fromTokenAmount.mul(adjustedDecimalsRatio).div(PRECISION);
}
export function getSwapAmountsByFromValue(p) {
    var tokenIn = p.tokenIn, tokenOut = p.tokenOut, amountIn = p.amountIn, triggerRatio = p.triggerRatio, isLimit = p.isLimit, findSwapPath = p.findSwapPath;
    var priceIn = tokenIn.prices.minPrice;
    var priceOut = tokenOut.prices.maxPrice;
    var usdIn = convertToUsd(amountIn, tokenIn.decimals, priceIn);
    var amountOut = BigNumber.from(0);
    var usdOut = BigNumber.from(0);
    var minOutputAmount = BigNumber.from(0);
    var defaultAmounts = {
        amountIn: amountIn,
        usdIn: usdIn,
        amountOut: amountOut,
        usdOut: usdOut,
        minOutputAmount: minOutputAmount,
        priceIn: priceIn,
        priceOut: priceOut,
        swapPathStats: undefined,
    };
    if (amountIn.lte(0)) {
        return defaultAmounts;
    }
    if (getIsEquivalentTokens(tokenIn, tokenOut)) {
        amountOut = amountIn;
        usdOut = usdIn;
        minOutputAmount = amountOut;
        return {
            amountIn: amountIn,
            usdIn: usdIn,
            amountOut: amountOut,
            usdOut: usdOut,
            minOutputAmount: minOutputAmount,
            priceIn: priceIn,
            priceOut: priceOut,
            swapPathStats: undefined,
        };
    }
    var swapPathStats = findSwapPath(defaultAmounts.usdIn, { byLiquidity: isLimit });
    if (!swapPathStats) {
        return defaultAmounts;
    }
    if (isLimit) {
        if (!triggerRatio) {
            return defaultAmounts;
        }
        amountOut = getAmountByRatio({
            fromToken: tokenIn,
            toToken: tokenOut,
            fromTokenAmount: amountIn,
            ratio: triggerRatio.ratio,
            shouldInvertRatio: triggerRatio.largestToken.value === tokenOut.value,
        });
        usdOut = convertToUsd(amountOut, tokenOut.decimals, priceOut);
        usdOut = usdOut.sub(swapPathStats.totalSwapFeeUsd).add(swapPathStats.totalSwapPriceImpactDeltaUsd);
        amountOut = convertToTokenAmount(usdOut, tokenOut.decimals, priceOut);
        minOutputAmount = amountOut;
    }
    else {
        usdOut = swapPathStats.usdOut;
        amountOut = swapPathStats.amountOut;
        minOutputAmount = amountOut;
    }
    if (amountOut.lt(0)) {
        amountOut = BigNumber.from(0);
        usdOut = BigNumber.from(0);
        minOutputAmount = BigNumber.from(0);
    }
    return {
        amountIn: amountIn,
        usdIn: usdIn,
        amountOut: amountOut,
        usdOut: usdOut,
        priceIn: priceIn,
        priceOut: priceOut,
        minOutputAmount: minOutputAmount,
        swapPathStats: swapPathStats,
    };
}
export function getSwapAmountsByToValue(p) {
    var tokenIn = p.tokenIn, tokenOut = p.tokenOut, amountOut = p.amountOut, triggerRatio = p.triggerRatio, isLimit = p.isLimit, findSwapPath = p.findSwapPath;
    var priceIn = tokenIn.prices.minPrice;
    var priceOut = tokenOut.prices.maxPrice;
    var usdOut = convertToUsd(amountOut, tokenOut.decimals, priceOut);
    var minOutputAmount = amountOut;
    var amountIn = BigNumber.from(0);
    var usdIn = BigNumber.from(0);
    var defaultAmounts = {
        amountIn: amountIn,
        usdIn: usdIn,
        amountOut: amountOut,
        usdOut: usdOut,
        minOutputAmount: minOutputAmount,
        priceIn: priceIn,
        priceOut: priceOut,
        swapPathStats: undefined,
    };
    if (amountOut.lte(0)) {
        return defaultAmounts;
    }
    if (getIsEquivalentTokens(tokenIn, tokenOut)) {
        amountIn = amountOut;
        usdIn = usdOut;
        return {
            amountIn: amountIn,
            usdIn: usdIn,
            amountOut: amountOut,
            usdOut: usdOut,
            minOutputAmount: minOutputAmount,
            priceIn: priceIn,
            priceOut: priceOut,
            swapPathStats: undefined,
        };
    }
    var baseUsdIn = usdOut;
    var swapPathStats = findSwapPath(baseUsdIn, { byLiquidity: isLimit });
    if (!swapPathStats) {
        return defaultAmounts;
    }
    if (isLimit) {
        if (!triggerRatio) {
            return defaultAmounts;
        }
        amountIn = getAmountByRatio({
            fromToken: tokenOut,
            toToken: tokenIn,
            fromTokenAmount: amountOut,
            ratio: triggerRatio.ratio,
            shouldInvertRatio: triggerRatio.largestToken.value === tokenIn.value,
        });
        usdIn = convertToUsd(amountIn, tokenIn.decimals, priceIn);
        usdIn = usdIn.add(swapPathStats.totalSwapFeeUsd).sub(swapPathStats.totalSwapPriceImpactDeltaUsd);
        amountIn = convertToTokenAmount(usdIn, tokenIn.decimals, priceIn);
    }
    else {
        var adjustedUsdIn = swapPathStats.usdOut.gt(0)
            ? baseUsdIn.mul(usdOut).div(swapPathStats.usdOut)
            : BigNumber.from(0);
        usdIn = adjustedUsdIn;
        amountIn = convertToTokenAmount(usdIn, tokenIn.decimals, priceIn);
    }
    if (amountIn.lt(0)) {
        amountIn = BigNumber.from(0);
        usdIn = BigNumber.from(0);
    }
    return {
        amountIn: amountIn,
        usdIn: usdIn,
        amountOut: amountOut,
        usdOut: usdOut,
        minOutputAmount: minOutputAmount,
        priceIn: priceIn,
        priceOut: priceOut,
        swapPathStats: swapPathStats,
    };
}
function getNextOpenInterestParams(p) {
    var currentLongUsd = p.currentLongUsd, currentShortUsd = p.currentShortUsd, usdDelta = p.usdDelta, isLong = p.isLong;
    var nextLongUsd = currentLongUsd;
    var nextShortUsd = currentShortUsd;
    if (isLong) {
        nextLongUsd = currentLongUsd === null || currentLongUsd === void 0 ? void 0 : currentLongUsd.add(usdDelta || 0);
    }
    else {
        nextShortUsd = currentShortUsd === null || currentShortUsd === void 0 ? void 0 : currentShortUsd.add(usdDelta || 0);
    }
    return {
        currentLongUsd: currentLongUsd,
        currentShortUsd: currentShortUsd,
        nextLongUsd: nextLongUsd,
        nextShortUsd: nextShortUsd,
    };
}
function getNextOpenInterestForVirtualInventory(p) {
    var virtualInventory = p.virtualInventory, usdDelta = p.usdDelta, isLong = p.isLong;
    var currentLongUsd = BigNumber.from(0);
    var currentShortUsd = BigNumber.from(0);
    if (virtualInventory.gt(0)) {
        currentShortUsd = virtualInventory;
    }
    else {
        currentLongUsd = virtualInventory.mul(-1);
    }
    if (usdDelta.lt(0)) {
        var offset = usdDelta.abs();
        currentLongUsd = currentLongUsd.add(offset);
        currentShortUsd = currentShortUsd.add(offset);
    }
    return getNextOpenInterestParams({
        currentLongUsd: currentLongUsd,
        currentShortUsd: currentShortUsd,
        usdDelta: usdDelta,
        isLong: isLong,
    });
}
export function getPriceImpactForPosition(marketInfo, sizeDeltaUsd, isLong, opts) {
    if (opts === void 0) { opts = {}; }
    var longInterestUsd = marketInfo.longInterestUsd, shortInterestUsd = marketInfo.shortInterestUsd;
    var _a = getNextOpenInterestParams({
        currentLongUsd: longInterestUsd,
        currentShortUsd: shortInterestUsd,
        usdDelta: sizeDeltaUsd,
        isLong: isLong,
    }), currentLongUsd = _a.currentLongUsd, currentShortUsd = _a.currentShortUsd, nextLongUsd = _a.nextLongUsd, nextShortUsd = _a.nextShortUsd;
    var priceImpactUsd = getPriceImpactUsd({
        currentLongUsd: currentLongUsd,
        currentShortUsd: currentShortUsd,
        nextLongUsd: nextLongUsd,
        nextShortUsd: nextShortUsd,
        factorPositive: marketInfo.positionImpactFactorPositive,
        factorNegative: marketInfo.positionImpactFactorNegative,
        exponentFactor: marketInfo.positionImpactExponentFactor,
        fallbackToZero: opts.fallbackToZero,
    });
    if (priceImpactUsd.gt(0)) {
        return priceImpactUsd;
    }
    if (!marketInfo.virtualInventoryForPositions.abs().gt(0)) {
        return priceImpactUsd;
    }
    var virtualInventoryParams = getNextOpenInterestForVirtualInventory({
        virtualInventory: marketInfo.virtualInventoryForPositions,
        usdDelta: sizeDeltaUsd,
        isLong: isLong,
    });
    var priceImpactUsdForVirtualInventory = getPriceImpactUsd({
        currentLongUsd: virtualInventoryParams.currentLongUsd,
        currentShortUsd: virtualInventoryParams.currentShortUsd,
        nextLongUsd: virtualInventoryParams.nextLongUsd,
        nextShortUsd: virtualInventoryParams.nextShortUsd,
        factorPositive: marketInfo.positionImpactFactorPositive,
        factorNegative: marketInfo.positionImpactFactorNegative,
        exponentFactor: marketInfo.positionImpactExponentFactor,
        fallbackToZero: opts.fallbackToZero,
    });
    return priceImpactUsdForVirtualInventory.lt(priceImpactUsd) ? priceImpactUsdForVirtualInventory : priceImpactUsd;
}
export function getPositionFee(marketInfo, sizeDeltaUsd, forPositiveImpact, referralInfo) {
    var factor = forPositiveImpact
        ? marketInfo.positionFeeFactorForPositiveImpact
        : marketInfo.positionFeeFactorForNegativeImpact;
    var positionFeeUsd = applyFactor(sizeDeltaUsd, factor);
    if (!referralInfo) {
        return { positionFeeUsd: positionFeeUsd, discountUsd: BigNumber.from(0), totalRebateUsd: BigNumber.from(0) };
    }
    var totalRebateUsd = applyFactor(positionFeeUsd, referralInfo.totalRebateFactor);
    var discountUsd = applyFactor(totalRebateUsd, referralInfo.discountFactor);
    positionFeeUsd = positionFeeUsd.sub(discountUsd);
    return {
        positionFeeUsd: positionFeeUsd,
        discountUsd: discountUsd,
        totalRebateUsd: totalRebateUsd,
    };
}
export function getPositionPendingFeesUsd(p) {
    var pendingFundingFeesUsd = p.pendingFundingFeesUsd, pendingBorrowingFeesUsd = p.pendingBorrowingFeesUsd;
    return pendingBorrowingFeesUsd.add(pendingFundingFeesUsd);
}
export function getLeverage(p) {
    var pnl = p.pnl, sizeInUsd = p.sizeInUsd, collateralUsd = p.collateralUsd, pendingBorrowingFeesUsd = p.pendingBorrowingFeesUsd, pendingFundingFeesUsd = p.pendingFundingFeesUsd;
    var totalPendingFeesUsd = getPositionPendingFeesUsd({ pendingFundingFeesUsd: pendingFundingFeesUsd, pendingBorrowingFeesUsd: pendingBorrowingFeesUsd });
    var remainingCollateralUsd = collateralUsd.add(pnl || 0).sub(totalPendingFeesUsd);
    if (remainingCollateralUsd.lte(0)) {
        return undefined;
    }
    return sizeInUsd.mul(BASIS_POINTS_DIVISOR).div(remainingCollateralUsd);
}
export function getPriceImpactByAcceptablePrice(p) {
    var sizeDeltaUsd = p.sizeDeltaUsd, acceptablePrice = p.acceptablePrice, markPrice = p.indexPrice, isLong = p.isLong, isIncrease = p.isIncrease;
    var shouldFlipPriceDiff = isIncrease ? !isLong : isLong;
    var priceDiff = markPrice.sub(acceptablePrice).mul(shouldFlipPriceDiff ? -1 : 1);
    var priceImpactDeltaUsd = sizeDeltaUsd.mul(priceDiff).div(acceptablePrice);
    var priceImpactDeltaAmount = priceImpactDeltaUsd.div(markPrice);
    return {
        priceImpactDeltaUsd: priceImpactDeltaUsd,
        priceImpactDeltaAmount: priceImpactDeltaAmount,
    };
}
export function getCappedPositionImpactUsd(marketInfo, sizeDeltaUsd, isLong, opts) {
    if (opts === void 0) { opts = {}; }
    var priceImpactDeltaUsd = getPriceImpactForPosition(marketInfo, sizeDeltaUsd, isLong, opts);
    if (priceImpactDeltaUsd.lt(0)) {
        return priceImpactDeltaUsd;
    }
    var indexToken = marketInfo.indexToken;
    var impactPoolAmount = marketInfo === null || marketInfo === void 0 ? void 0 : marketInfo.positionImpactPoolAmount;
    var maxPriceImpactUsdBasedOnImpactPool = convertToUsd(impactPoolAmount, indexToken.decimals, indexToken.prices.minPrice);
    var cappedImpactUsd = priceImpactDeltaUsd;
    if (cappedImpactUsd.gt(maxPriceImpactUsdBasedOnImpactPool)) {
        cappedImpactUsd = maxPriceImpactUsdBasedOnImpactPool;
    }
    var maxPriceImpactFactor = marketInfo.maxPositionImpactFactorPositive;
    var maxPriceImpactUsdBasedOnMaxPriceImpactFactor = applyFactor(sizeDeltaUsd.abs(), maxPriceImpactFactor);
    if (cappedImpactUsd.gt(maxPriceImpactUsdBasedOnMaxPriceImpactFactor)) {
        cappedImpactUsd = maxPriceImpactUsdBasedOnMaxPriceImpactFactor;
    }
    return cappedImpactUsd;
}
export function getBasisPoints(numerator, denominator) {
    return numerator.mul(BASIS_POINTS_DIVISOR).div(denominator);
}
export function getAcceptablePriceInfo(p) {
    var marketInfo = p.marketInfo, isIncrease = p.isIncrease, isLong = p.isLong, indexPrice = p.indexPrice, sizeDeltaUsd = p.sizeDeltaUsd, maxNegativePriceImpactBps = p.maxNegativePriceImpactBps;
    var indexToken = marketInfo.indexToken;
    var values = {
        acceptablePrice: BigNumber.from(0),
        acceptablePriceDeltaBps: BigNumber.from(0),
        priceImpactDeltaAmount: BigNumber.from(0),
        priceImpactDeltaUsd: BigNumber.from(0),
        priceImpactDiffUsd: BigNumber.from(0),
    };
    if (!sizeDeltaUsd.gt(0)) {
        return values;
    }
    var shouldFlipPriceImpact = getShouldUseMaxPrice(p.isIncrease, p.isLong);
    // For Limit / Trigger orders
    if (maxNegativePriceImpactBps === null || maxNegativePriceImpactBps === void 0 ? void 0 : maxNegativePriceImpactBps.gt(0)) {
        var priceDelta_1 = indexPrice.mul(maxNegativePriceImpactBps).div(BASIS_POINTS_DIVISOR);
        priceDelta_1 = shouldFlipPriceImpact ? priceDelta_1 === null || priceDelta_1 === void 0 ? void 0 : priceDelta_1.mul(-1) : priceDelta_1;
        values.acceptablePrice = indexPrice.sub(priceDelta_1);
        values.acceptablePriceDeltaBps = maxNegativePriceImpactBps.mul(-1);
        var priceImpact = getPriceImpactByAcceptablePrice({
            sizeDeltaUsd: sizeDeltaUsd,
            acceptablePrice: values.acceptablePrice,
            indexPrice: indexPrice,
            isLong: isLong,
            isIncrease: isIncrease,
        });
        values.priceImpactDeltaUsd = priceImpact.priceImpactDeltaUsd;
        values.priceImpactDeltaAmount = priceImpact.priceImpactDeltaAmount;
        return values;
    }
    values.priceImpactDeltaUsd = getCappedPositionImpactUsd(marketInfo, isIncrease ? sizeDeltaUsd : sizeDeltaUsd.mul(-1), isLong, {
        fallbackToZero: !isIncrease,
    });
    if (!isIncrease && values.priceImpactDeltaUsd.lt(0)) {
        var minPriceImpactUsd = applyFactor(sizeDeltaUsd, marketInfo.maxPositionImpactFactorNegative).mul(-1);
        if (values.priceImpactDeltaUsd.lt(minPriceImpactUsd)) {
            values.priceImpactDiffUsd = minPriceImpactUsd.sub(values.priceImpactDeltaUsd);
            values.priceImpactDeltaUsd = minPriceImpactUsd;
        }
    }
    if (values.priceImpactDeltaUsd.gt(0)) {
        values.priceImpactDeltaAmount = convertToTokenAmount(values.priceImpactDeltaUsd, indexToken.decimals, indexToken.prices.maxPrice);
    }
    else {
        values.priceImpactDeltaAmount = roundUpMagnitudeDivision(values.priceImpactDeltaUsd.mul(expandDecimals(1, indexToken.decimals)), indexToken.prices.minPrice);
    }
    var priceImpactForPriceAdjustment = shouldFlipPriceImpact
        ? values.priceImpactDeltaUsd.mul(-1)
        : values.priceImpactDeltaUsd;
    values.acceptablePrice = indexPrice.mul(sizeDeltaUsd.add(priceImpactForPriceAdjustment)).div(sizeDeltaUsd);
    var priceDelta = indexPrice.sub(values.acceptablePrice).mul(shouldFlipPriceImpact ? 1 : -1);
    values.acceptablePriceDeltaBps = getBasisPoints(priceDelta, p.indexPrice);
    return values;
}
export function getIncreasePositionAmounts(p) {
    var marketInfo = p.marketInfo, indexToken = p.indexToken, initialCollateralToken = p.initialCollateralToken, collateralToken = p.collateralToken, initialCollateralAmount = p.initialCollateralAmount, indexTokenAmount = p.indexTokenAmount, isLong = p.isLong, leverage = p.leverage, triggerPrice = p.triggerPrice, position = p.position, savedAcceptablePriceImpactBps = p.savedAcceptablePriceImpactBps, findSwapPath = p.findSwapPath, userReferralInfo = p.userReferralInfo, strategy = p.strategy;
    var values = {
        initialCollateralAmount: BigNumber.from(0),
        initialCollateralUsd: BigNumber.from(0),
        collateralDeltaAmount: BigNumber.from(0),
        collateralDeltaUsd: BigNumber.from(0),
        swapPathStats: undefined,
        indexTokenAmount: BigNumber.from(0),
        sizeDeltaUsd: BigNumber.from(0),
        sizeDeltaInTokens: BigNumber.from(0),
        estimatedLeverage: BigNumber.from(0),
        indexPrice: BigNumber.from(0),
        initialCollateralPrice: BigNumber.from(0),
        collateralPrice: BigNumber.from(0),
        triggerPrice: BigNumber.from(0),
        acceptablePrice: BigNumber.from(0),
        acceptablePriceDeltaBps: BigNumber.from(0),
        positionFeeUsd: BigNumber.from(0),
        feeDiscountUsd: BigNumber.from(0),
        borrowingFeeUsd: BigNumber.from(0),
        fundingFeeUsd: BigNumber.from(0),
        positionPriceImpactDeltaUsd: BigNumber.from(0),
    };
    var isLimit = triggerPrice === null || triggerPrice === void 0 ? void 0 : triggerPrice.gt(0);
    if (triggerPrice === null || triggerPrice === void 0 ? void 0 : triggerPrice.gt(0)) {
        values.triggerPrice = triggerPrice;
        values.triggerThresholdType = getTriggerThresholdType(OrderType.LimitIncrease, isLong);
        values.indexPrice = triggerPrice;
        values.initialCollateralPrice = getIsEquivalentTokens(indexToken, initialCollateralToken)
            ? triggerPrice
            : initialCollateralToken.prices.minPrice;
        values.collateralPrice = getIsEquivalentTokens(indexToken, collateralToken)
            ? triggerPrice
            : collateralToken.prices.minPrice;
    }
    else {
        values.indexPrice = getMarkPrice({ prices: indexToken.prices, isIncrease: true, isLong: isLong });
        values.initialCollateralPrice = initialCollateralToken.prices.minPrice;
        values.collateralPrice = collateralToken.prices.minPrice;
    }
    values.borrowingFeeUsd = (position === null || position === void 0 ? void 0 : position.pendingBorrowingFeesUsd) || BigNumber.from(0);
    values.fundingFeeUsd = (position === null || position === void 0 ? void 0 : position.pendingFundingFeesUsd) || BigNumber.from(0);
    if (!values.indexPrice.gt(0) || !values.initialCollateralPrice.gt(0) || !values.collateralPrice.gt(0)) {
        return values;
    }
    // Size and collateral
    if (strategy === 'leverageByCollateral' && leverage && (initialCollateralAmount === null || initialCollateralAmount === void 0 ? void 0 : initialCollateralAmount.gt(0))) {
        values.estimatedLeverage = leverage;
        values.initialCollateralAmount = initialCollateralAmount;
        values.initialCollateralUsd = convertToUsd(initialCollateralAmount, initialCollateralToken.decimals, values.initialCollateralPrice);
        // TODO: collateralPrice?
        var swapAmounts = getSwapAmountsByFromValue({
            tokenIn: initialCollateralToken,
            tokenOut: collateralToken,
            amountIn: initialCollateralAmount,
            isLimit: false,
            findSwapPath: findSwapPath,
        });
        values.swapPathStats = swapAmounts.swapPathStats;
        var baseCollateralUsd = convertToUsd(swapAmounts.amountOut, collateralToken.decimals, values.collateralPrice);
        var baseSizeDeltaUsd = baseCollateralUsd.mul(leverage).div(BASIS_POINTS_DIVISOR);
        var basePriceImpactDeltaUsd = getPriceImpactForPosition(marketInfo, baseSizeDeltaUsd, isLong);
        var basePositionFeeInfo = getPositionFee(marketInfo, baseSizeDeltaUsd, basePriceImpactDeltaUsd.gt(0), userReferralInfo);
        values.sizeDeltaUsd = baseCollateralUsd
            .sub(basePositionFeeInfo.positionFeeUsd)
            .mul(leverage)
            .div(BASIS_POINTS_DIVISOR);
        values.indexTokenAmount = convertToTokenAmount(values.sizeDeltaUsd, indexToken.decimals, values.indexPrice);
        var positionFeeInfo = getPositionFee(marketInfo, values.sizeDeltaUsd, basePriceImpactDeltaUsd.gt(0), userReferralInfo);
        values.positionFeeUsd = positionFeeInfo.positionFeeUsd;
        values.feeDiscountUsd = positionFeeInfo.discountUsd;
        values.collateralDeltaUsd = baseCollateralUsd
            .sub(values.positionFeeUsd)
            .sub(values.borrowingFeeUsd)
            .sub(values.fundingFeeUsd);
        values.collateralDeltaAmount = convertToTokenAmount(values.collateralDeltaUsd, collateralToken.decimals, values.collateralPrice);
    }
    else if (strategy === 'leverageBySize' && leverage && (indexTokenAmount === null || indexTokenAmount === void 0 ? void 0 : indexTokenAmount.gt(0))) {
        values.estimatedLeverage = leverage;
        values.indexTokenAmount = indexTokenAmount;
        values.sizeDeltaUsd = convertToUsd(indexTokenAmount, indexToken.decimals, values.indexPrice);
        var basePriceImpactDeltaUsd = getPriceImpactForPosition(marketInfo, values.sizeDeltaUsd, isLong);
        var positionFeeInfo = getPositionFee(marketInfo, values.sizeDeltaUsd, basePriceImpactDeltaUsd.gt(0), userReferralInfo);
        values.positionFeeUsd = positionFeeInfo.positionFeeUsd;
        values.feeDiscountUsd = positionFeeInfo.discountUsd;
        values.collateralDeltaUsd = values.sizeDeltaUsd.mul(BASIS_POINTS_DIVISOR).div(leverage);
        values.collateralDeltaAmount = convertToTokenAmount(values.collateralDeltaUsd, collateralToken.decimals, values.collateralPrice);
        var baseCollateralUsd = values.collateralDeltaUsd
            .add(values.positionFeeUsd)
            .add(values.borrowingFeeUsd)
            .add(values.fundingFeeUsd);
        var baseCollateralAmount = convertToTokenAmount(baseCollateralUsd, collateralToken.decimals, values.collateralPrice);
        // TODO: collateralPrice?
        var swapAmounts = getSwapAmountsByToValue({
            tokenIn: initialCollateralToken,
            tokenOut: collateralToken,
            amountOut: baseCollateralAmount,
            isLimit: false,
            findSwapPath: findSwapPath,
        });
        values.swapPathStats = swapAmounts.swapPathStats;
        values.initialCollateralAmount = swapAmounts.amountIn;
        values.initialCollateralUsd = convertToUsd(values.initialCollateralAmount, initialCollateralToken.decimals, values.initialCollateralPrice);
    }
    else if (strategy === 'independent') {
        if (indexTokenAmount === null || indexTokenAmount === void 0 ? void 0 : indexTokenAmount.gt(0)) {
            values.indexTokenAmount = indexTokenAmount;
            values.sizeDeltaUsd = convertToUsd(indexTokenAmount, indexToken.decimals, values.indexPrice);
            var basePriceImpactDeltaUsd = getPriceImpactForPosition(marketInfo, values.sizeDeltaUsd, isLong);
            var positionFeeInfo = getPositionFee(marketInfo, values.sizeDeltaUsd, basePriceImpactDeltaUsd.gt(0), userReferralInfo);
            values.positionFeeUsd = positionFeeInfo.positionFeeUsd;
            values.feeDiscountUsd = positionFeeInfo.discountUsd;
        }
        if (initialCollateralAmount === null || initialCollateralAmount === void 0 ? void 0 : initialCollateralAmount.gt(0)) {
            values.initialCollateralAmount = initialCollateralAmount;
            values.initialCollateralUsd = convertToUsd(initialCollateralAmount, initialCollateralToken.decimals, values.initialCollateralPrice);
            // TODO: collateralPrice?
            var swapAmounts = getSwapAmountsByFromValue({
                tokenIn: initialCollateralToken,
                tokenOut: collateralToken,
                amountIn: initialCollateralAmount,
                isLimit: false,
                findSwapPath: findSwapPath,
            });
            values.swapPathStats = swapAmounts.swapPathStats;
            var baseCollateralUsd = convertToUsd(swapAmounts.amountOut, collateralToken.decimals, values.collateralPrice);
            values.collateralDeltaUsd = baseCollateralUsd
                .sub(values.positionFeeUsd)
                .sub(values.borrowingFeeUsd)
                .sub(values.fundingFeeUsd);
            values.collateralDeltaAmount = convertToTokenAmount(values.collateralDeltaUsd, collateralToken.decimals, values.collateralPrice);
        }
        values.estimatedLeverage = getLeverage({
            sizeInUsd: values.sizeDeltaUsd,
            collateralUsd: values.collateralDeltaUsd,
            pnl: BigNumber.from(0),
            pendingBorrowingFeesUsd: BigNumber.from(0),
            pendingFundingFeesUsd: BigNumber.from(0),
        });
    }
    var acceptablePriceInfo = getAcceptablePriceInfo({
        marketInfo: marketInfo,
        isIncrease: true,
        isLong: isLong,
        indexPrice: values.indexPrice,
        sizeDeltaUsd: values.sizeDeltaUsd,
    });
    values.positionPriceImpactDeltaUsd = acceptablePriceInfo.priceImpactDeltaUsd;
    values.acceptablePrice = acceptablePriceInfo.acceptablePrice;
    values.acceptablePriceDeltaBps = acceptablePriceInfo.acceptablePriceDeltaBps;
    if (isLimit) {
        var limitAcceptablePriceInfo = getAcceptablePriceInfo({
            marketInfo: marketInfo,
            isIncrease: true,
            isLong: isLong,
            indexPrice: values.indexPrice,
            sizeDeltaUsd: values.sizeDeltaUsd,
            maxNegativePriceImpactBps: savedAcceptablePriceImpactBps,
        });
        values.acceptablePrice = limitAcceptablePriceInfo.acceptablePrice;
        values.acceptablePriceDeltaBps = limitAcceptablePriceInfo.acceptablePriceDeltaBps;
        if (values.positionPriceImpactDeltaUsd.lt(limitAcceptablePriceInfo.priceImpactDeltaUsd)) {
            values.positionPriceImpactDeltaUsd = limitAcceptablePriceInfo.priceImpactDeltaUsd;
        }
    }
    var priceImpactAmount = BigNumber.from(0);
    if (values.positionPriceImpactDeltaUsd.gt(0)) {
        var price = (triggerPrice === null || triggerPrice === void 0 ? void 0 : triggerPrice.gt(0)) ? triggerPrice : indexToken.prices.maxPrice;
        priceImpactAmount = convertToTokenAmount(values.positionPriceImpactDeltaUsd, indexToken.decimals, price);
    }
    else {
        var price = (triggerPrice === null || triggerPrice === void 0 ? void 0 : triggerPrice.gt(0)) ? triggerPrice : indexToken.prices.minPrice;
        priceImpactAmount = convertToTokenAmount(values.positionPriceImpactDeltaUsd, indexToken.decimals, price);
    }
    values.sizeDeltaInTokens = convertToTokenAmount(values.sizeDeltaUsd, indexToken.decimals, values.indexPrice);
    if (isLong) {
        values.sizeDeltaInTokens = values.sizeDeltaInTokens.add(priceImpactAmount);
    }
    else {
        values.sizeDeltaInTokens = values.sizeDeltaInTokens.sub(priceImpactAmount);
    }
    return values;
}
export function getTokenData(tokensData, address, convertTo) {
    if (!address || !(tokensData === null || tokensData === void 0 ? void 0 : tokensData[address])) {
        return undefined;
    }
    var token = tokensData[address];
    if (convertTo === 'wrapped' && token.isNative && token.wrappedAddress) {
        return tokensData[token.wrappedAddress];
    }
    if (convertTo === 'native' && token.isWrapped) {
        return tokensData[NATIVE_TOKEN_ADDRESS];
    }
    return token;
}
export function adaptToV1TokenInfo(tokenData) {
    var _a, _b;
    return __assign(__assign({}, tokenData), { minPrice: (_a = tokenData.prices) === null || _a === void 0 ? void 0 : _a.minPrice, maxPrice: (_b = tokenData.prices) === null || _b === void 0 ? void 0 : _b.maxPrice });
}
export function adaptToV1InfoTokens(tokensData) {
    var infoTokens = Object.keys(tokensData).reduce(function (acc, address) {
        var tokenData = getTokenData(tokensData, address);
        acc[address] = adaptToV1TokenInfo(tokenData);
        return acc;
    }, {});
    return infoTokens;
}
export var getAvailabeTokenData = function (_a) {
    var e_6, _b;
    var chainId = _a.chainId, marketsInfoData = _a.marketsInfoData, tokensData = _a.tokensData;
    var marketsInfo = Object.values(marketsInfoData || {})
        .filter(function (market) { return !market.isDisabled; })
        .sort(function (a, b) {
        return a.indexToken.symbol.localeCompare(b.indexToken.symbol);
    });
    var allMarkets = new Set();
    var tokensMap = getTokensMap(chainId);
    var nativeToken = getByKey(tokensData, NATIVE_TOKEN_ADDRESS);
    var indexTokens = new Set();
    var indexTokensWithPoolValue = {};
    var collaterals = new Set();
    var longTokensWithPoolValue = {};
    var shortTokensWithPoolValue = {};
    try {
        for (var marketsInfo_1 = __values(marketsInfo), marketsInfo_1_1 = marketsInfo_1.next(); !marketsInfo_1_1.done; marketsInfo_1_1 = marketsInfo_1.next()) {
            var marketInfo = marketsInfo_1_1.value;
            var longToken = marketInfo.longToken;
            var shortToken = marketInfo.shortToken;
            var indexToken = marketInfo.indexToken;
            if (marketInfo.isDisabled || !longToken || !shortToken || !indexToken) {
                continue;
            }
            if ((longToken.isWrapped || shortToken.isWrapped) && nativeToken) {
                collaterals.add(nativeToken);
            }
            collaterals.add(longToken);
            collaterals.add(shortToken);
            var longPoolAmountUsd = convertToUsd(marketInfo.longPoolAmount, marketInfo.longToken.decimals, getMidPrice(marketInfo.longToken.prices));
            var shortPoolAmountUsd = convertToUsd(marketInfo.shortPoolAmount, marketInfo.shortToken.decimals, getMidPrice(marketInfo.shortToken.prices));
            longTokensWithPoolValue[longToken.value] = (longTokensWithPoolValue[longToken.value] || BigNumber.from(0)).add(longPoolAmountUsd);
            shortTokensWithPoolValue[shortToken.value] = (shortTokensWithPoolValue[shortToken.value] || BigNumber.from(0)).add(shortPoolAmountUsd);
            if (!marketInfo.isSpotOnly) {
                indexTokens.add(indexToken);
                allMarkets.add(marketInfo);
                indexTokensWithPoolValue[indexToken.value] = (indexTokensWithPoolValue[indexToken.value] || BigNumber.from(0)).add(marketInfo.poolValueMax);
            }
        }
    }
    catch (e_6_1) { e_6 = { error: e_6_1 }; }
    finally {
        try {
            if (marketsInfo_1_1 && !marketsInfo_1_1.done && (_b = marketsInfo_1.return)) _b.call(marketsInfo_1);
        }
        finally { if (e_6) throw e_6.error; }
    }
    var sortedIndexTokensWithPoolValue = Object.keys(indexTokensWithPoolValue).sort(function (a, b) {
        return indexTokensWithPoolValue[b].gt(indexTokensWithPoolValue[a]) ? 1 : -1;
    });
    var sortedAllMarkets = Array.from(allMarkets).sort(function (a, b) {
        return (sortedIndexTokensWithPoolValue.indexOf(a.indexToken.value) -
            sortedIndexTokensWithPoolValue.indexOf(b.indexToken.value));
    });
    var sortedLongTokens = Object.keys(longTokensWithPoolValue).sort(function (a, b) {
        return longTokensWithPoolValue[b].gt(longTokensWithPoolValue[a]) ? 1 : -1;
    });
    var sortedShortTokens = Object.keys(shortTokensWithPoolValue).sort(function (a, b) {
        return shortTokensWithPoolValue[b].gt(shortTokensWithPoolValue[a]) ? 1 : -1;
    });
    var sortedLongAndShortTokens = sortedLongTokens.concat(sortedShortTokens);
    return {
        tokensMap: tokensMap,
        swapTokens: Array.from(collaterals),
        indexTokens: Array.from(indexTokens),
        infoTokens: adaptToV1InfoTokens(tokensData || {}),
        sortedIndexTokensWithPoolValue: sortedIndexTokensWithPoolValue,
        sortedLongAndShortTokens: sortedLongAndShortTokens,
        sortedAllMarkets: sortedAllMarkets,
    };
};
export var ACCOUNT_POSITION_LIST_KEY = hashString('ACCOUNT_POSITION_LIST');
export function accountPositionListKey(account) {
    return hashData(['bytes32', 'address'], [ACCOUNT_POSITION_LIST_KEY, account]);
}
export function getTradeFlags(tradeType, tradeMode) {
    var isLong = tradeType === TradeType.Long;
    var isShort = tradeType === TradeType.Short;
    var isSwap = tradeType === TradeType.Swap;
    var isPosition = isLong || isShort;
    var isMarket = tradeMode === TradeMode.Market;
    var isLimit = tradeMode === TradeMode.Limit;
    var isTrigger = tradeMode === TradeMode.Trigger;
    var isIncrease = isPosition && (isMarket || isLimit);
    return {
        isLong: isLong,
        isShort: isShort,
        isSwap: isSwap,
        isPosition: isPosition,
        isIncrease: isIncrease,
        isMarket: isMarket,
        isLimit: isLimit,
        isTrigger: isTrigger,
    };
}
export function getPositionKey(account, marketAddress, collateralAddress, isLong) {
    return "".concat(account, ":").concat(marketAddress, ":").concat(collateralAddress, ":").concat(isLong);
}
export function hashedPositionKey(account, market, collateralToken, isLong) {
    return hashData(['address', 'address', 'address', 'bool'], [account, market, collateralToken, isLong]);
}
export var getKeysAndPricesParams = function (p) {
    var e_7, _a, e_8, _b, e_9, _c;
    var account = p.account, marketsInfoData = p.marketsInfoData, tokensData = p.tokensData, existingPositionsKeysSet = p.existingPositionsKeysSet;
    var values = {
        allPositionsKeys: [],
        contractPositionsKeys: [],
        marketsPrices: [],
    };
    if (!account || !marketsInfoData || !tokensData) {
        return values;
    }
    var markets = Object.values(marketsInfoData);
    try {
        for (var markets_2 = __values(markets), markets_2_1 = markets_2.next(); !markets_2_1.done; markets_2_1 = markets_2.next()) {
            var market = markets_2_1.value;
            var marketPrices = getContractMarketPrices(tokensData, market);
            if (!marketPrices) {
                continue;
            }
            var collaterals = market.isSameCollaterals
                ? [market.longTokenAddress]
                : [market.longTokenAddress, market.shortTokenAddress];
            try {
                for (var collaterals_1 = (e_8 = void 0, __values(collaterals)), collaterals_1_1 = collaterals_1.next(); !collaterals_1_1.done; collaterals_1_1 = collaterals_1.next()) {
                    var collateralAddress = collaterals_1_1.value;
                    try {
                        for (var _d = (e_9 = void 0, __values([true, false])), _e = _d.next(); !_e.done; _e = _d.next()) {
                            var isLong = _e.value;
                            var positionKey = getPositionKey(account, market.marketTokenAddress, collateralAddress, isLong);
                            values.allPositionsKeys.push(positionKey);
                            var contractPositionKey = hashedPositionKey(account, market.marketTokenAddress, collateralAddress, isLong);
                            if (existingPositionsKeysSet === null || existingPositionsKeysSet === void 0 ? void 0 : existingPositionsKeysSet.has(contractPositionKey)) {
                                values.contractPositionsKeys.push(contractPositionKey);
                                values.marketsPrices.push(marketPrices);
                            }
                        }
                    }
                    catch (e_9_1) { e_9 = { error: e_9_1 }; }
                    finally {
                        try {
                            if (_e && !_e.done && (_c = _d.return)) _c.call(_d);
                        }
                        finally { if (e_9) throw e_9.error; }
                    }
                }
            }
            catch (e_8_1) { e_8 = { error: e_8_1 }; }
            finally {
                try {
                    if (collaterals_1_1 && !collaterals_1_1.done && (_b = collaterals_1.return)) _b.call(collaterals_1);
                }
                finally { if (e_8) throw e_8.error; }
            }
        }
    }
    catch (e_7_1) { e_7 = { error: e_7_1 }; }
    finally {
        try {
            if (markets_2_1 && !markets_2_1.done && (_a = markets_2.return)) _a.call(markets_2);
        }
        finally { if (e_7) throw e_7.error; }
    }
    return values;
};
export function getFeeItem(feeDeltaUsd, basis) {
    if (!feeDeltaUsd)
        return undefined;
    return {
        deltaUsd: feeDeltaUsd,
        bps: (basis === null || basis === void 0 ? void 0 : basis.gt(0)) ? getBasisPoints(feeDeltaUsd, basis) : BigNumber.from(0),
    };
}
export function getTotalFeeItem(feeItems) {
    var totalFeeItem = {
        deltaUsd: BigNumber.from(0),
        bps: BigNumber.from(0),
    };
    feeItems.filter(Boolean).forEach(function (feeItem) {
        totalFeeItem.deltaUsd = totalFeeItem.deltaUsd.add(feeItem.deltaUsd);
        totalFeeItem.bps = totalFeeItem.bps.add(feeItem.bps);
    });
    return totalFeeItem;
}
export function getTradeFees(p) {
    var isIncrease = p.isIncrease, initialCollateralUsd = p.initialCollateralUsd, sizeDeltaUsd = p.sizeDeltaUsd, swapSteps = p.swapSteps, positionFeeUsd = p.positionFeeUsd, swapPriceImpactDeltaUsd = p.swapPriceImpactDeltaUsd, positionPriceImpactDeltaUsd = p.positionPriceImpactDeltaUsd, borrowingFeeUsd = p.borrowingFeeUsd, fundingFeeUsd = p.fundingFeeUsd, feeDiscountUsd = p.feeDiscountUsd, swapProfitFeeUsd = p.swapProfitFeeUsd;
    var swapFees = initialCollateralUsd.gt(0)
        ? swapSteps.map(function (step) { return ({
            tokenInAddress: step.tokenInAddress,
            tokenOutAddress: step.tokenOutAddress,
            marketAddress: step.marketAddress,
            deltaUsd: step.swapFeeUsd.mul(-1),
            bps: !step.usdIn.eq(0) ? getBasisPoints(step.swapFeeUsd.mul(-1), step.usdIn) : BigNumber.from(0),
        }); })
        : undefined;
    var swapProfitFee = getFeeItem(swapProfitFeeUsd.mul(-1), initialCollateralUsd);
    var swapPriceImpact = getFeeItem(swapPriceImpactDeltaUsd, initialCollateralUsd);
    var positionFeeBeforeDiscount = getFeeItem(positionFeeUsd.add(feeDiscountUsd).mul(-1), sizeDeltaUsd);
    var positionFeeAfterDiscount = getFeeItem(positionFeeUsd.mul(-1), sizeDeltaUsd);
    var borrowFee = getFeeItem(borrowingFeeUsd.mul(-1), initialCollateralUsd);
    var fundingFee = getFeeItem(fundingFeeUsd.mul(-1), initialCollateralUsd);
    var positionPriceImpact = getFeeItem(positionPriceImpactDeltaUsd, sizeDeltaUsd);
    var totalFees = getTotalFeeItem(__spreadArray(__spreadArray([], __read((swapFees || [])), false), [
        swapProfitFee,
        swapPriceImpact,
        positionFeeAfterDiscount,
        positionPriceImpact,
        borrowFee,
        fundingFee,
    ], false));
    var payTotalFees = getTotalFeeItem(__spreadArray(__spreadArray([], __read((swapFees || [])), false), [
        swapProfitFee,
        swapPriceImpact,
        positionFeeAfterDiscount,
        borrowFee,
        fundingFee,
        !isIncrease ? positionPriceImpact : undefined,
    ], false));
    return {
        totalFees: totalFees,
        payTotalFees: payTotalFees,
        swapFees: swapFees,
        swapProfitFee: swapProfitFee,
        swapPriceImpact: swapPriceImpact,
        positionFee: positionFeeBeforeDiscount,
        positionPriceImpact: positionPriceImpact,
        borrowFee: borrowFee,
        fundingFee: fundingFee,
        feeDiscountUsd: feeDiscountUsd,
    };
}
export function getIsWrap(token1, token2) {
    return token1.isNative && token2.isWrapped;
}
export function getIsUnwrap(token1, token2) {
    return token1.isWrapped && token2.isNative;
}
export function estimateExecuteSwapOrderGasLimit(gasLimits, order) {
    var _a;
    var swapsCount = ((_a = order.swapPath) === null || _a === void 0 ? void 0 : _a.length) || 0;
    return gasLimits.swapOrder.add(gasLimits.singleSwap.mul(swapsCount)).add(order.callbackGasLimit || 0);
}
export function estimateExecuteIncreaseOrderGasLimit(gasLimits, order) {
    var _a;
    var swapsCount = ((_a = order.swapPath) === null || _a === void 0 ? void 0 : _a.length) || 0;
    return gasLimits.increaseOrder.add(gasLimits.singleSwap.mul(swapsCount)).add(order.callbackGasLimit || 0);
}
export function getHighExecutionFee(chainId) {
    return HIGH_EXECUTION_FEES_MAP[chainId] || 3;
}
export function getExecutionFee(chainId, gasLimts, tokensData, estimatedGasLimit, gasPrice) {
    var nativeToken = getTokenData(tokensData, NATIVE_TOKEN_ADDRESS);
    if (!nativeToken)
        return undefined;
    var baseGasLimit = gasLimts.estimatedFeeBaseGasLimit;
    var multiplierFactor = gasLimts.estimatedFeeMultiplierFactor;
    var adjustedGasLimit = baseGasLimit.add(applyFactor(estimatedGasLimit, multiplierFactor));
    var feeTokenAmount = adjustedGasLimit.mul(gasPrice);
    var feeUsd = convertToUsd(feeTokenAmount, nativeToken.decimals, nativeToken.prices.minPrice);
    var isFeeHigh = feeUsd.gt(expandDecimals(getHighExecutionFee(chainId), USD_DECIMALS));
    var warning = isFeeHigh
        ? "The network cost to send transactions is high at the moment, please check the \"Max Execution Fee\" value before proceeding."
        : undefined;
    return {
        feeUsd: feeUsd,
        feeTokenAmount: feeTokenAmount,
        feeToken: nativeToken,
        warning: warning,
    };
}
export var getOptimisticPositions = function (_a) {
    var positionsData = _a.positionsData, allPositionsKeys = _a.allPositionsKeys;
    if (!allPositionsKeys) {
        return undefined;
    }
    return allPositionsKeys.reduce(function (acc, key) {
        var position;
        if (getByKey(positionsData, key)) {
            position = __assign({}, getByKey(positionsData, key));
        }
        else {
            return acc;
        }
        if (position.sizeInUsd.gt(0)) {
            acc[key] = position;
        }
        return acc;
    }, {});
};
export function getPositionValueUsd(p) {
    var indexToken = p.indexToken, sizeInTokens = p.sizeInTokens, markPrice = p.markPrice;
    return convertToUsd(sizeInTokens, indexToken.decimals, markPrice);
}
export function getPositionPnlUsd(p) {
    var marketInfo = p.marketInfo, sizeInUsd = p.sizeInUsd, sizeInTokens = p.sizeInTokens, markPrice = p.markPrice, isLong = p.isLong;
    var positionValueUsd = getPositionValueUsd({ indexToken: marketInfo.indexToken, sizeInTokens: sizeInTokens, markPrice: markPrice });
    var totalPnl = isLong ? positionValueUsd.sub(sizeInUsd) : sizeInUsd.sub(positionValueUsd);
    if (totalPnl.lte(0)) {
        return totalPnl;
    }
    var poolPnl = isLong ? p.marketInfo.pnlLongMax : p.marketInfo.pnlShortMax;
    var poolUsd = getPoolUsdWithoutPnl(marketInfo, isLong, 'minPrice');
    var cappedPnl = getCappedPoolPnl({
        marketInfo: marketInfo,
        poolUsd: poolUsd,
        isLong: isLong,
        maximize: true,
    });
    var WEI_PRECISION = expandDecimals(1, 18);
    if (!cappedPnl.eq(poolPnl) && cappedPnl.gt(0) && poolPnl.gt(0)) {
        totalPnl = totalPnl.mul(cappedPnl.div(WEI_PRECISION)).div(poolPnl.div(WEI_PRECISION));
    }
    return totalPnl;
}
export function getCappedPoolPnl(p) {
    var marketInfo = p.marketInfo, poolUsd = p.poolUsd, isLong = p.isLong, maximize = p.maximize;
    var poolPnl;
    if (isLong) {
        poolPnl = maximize ? marketInfo.pnlLongMax : marketInfo.pnlLongMin;
    }
    else {
        poolPnl = maximize ? marketInfo.pnlShortMax : marketInfo.pnlShortMin;
    }
    if (poolPnl.lt(0)) {
        return poolPnl;
    }
    var maxPnlFactor = isLong
        ? marketInfo.maxPnlFactorForTradersLong
        : marketInfo.maxPnlFactorForTradersShort;
    var maxPnl = applyFactor(poolUsd, maxPnlFactor);
    return poolPnl.gt(maxPnl) ? maxPnl : poolPnl;
}
export function getLiquidationPrice(p) {
    var sizeInUsd = p.sizeInUsd, sizeInTokens = p.sizeInTokens, collateralUsd = p.collateralUsd, collateralAmount = p.collateralAmount, marketInfo = p.marketInfo, collateralToken = p.collateralToken, pendingFundingFeesUsd = p.pendingFundingFeesUsd, pendingBorrowingFeesUsd = p.pendingBorrowingFeesUsd, minCollateralUsd = p.minCollateralUsd, isLong = p.isLong, userReferralInfo = p.userReferralInfo, useMaxPriceImpact = p.useMaxPriceImpact;
    if (!sizeInUsd.gt(0) || !sizeInTokens.gt(0)) {
        return undefined;
    }
    var indexToken = marketInfo.indexToken;
    var closingFeeUsd = getPositionFee(marketInfo, sizeInUsd, false, userReferralInfo).positionFeeUsd;
    var totalPendingFeesUsd = getPositionPendingFeesUsd({ pendingFundingFeesUsd: pendingFundingFeesUsd, pendingBorrowingFeesUsd: pendingBorrowingFeesUsd });
    var totalFeesUsd = totalPendingFeesUsd.add(closingFeeUsd);
    var maxNegativePriceImpactUsd = applyFactor(sizeInUsd, marketInfo.maxPositionImpactFactorForLiquidations).mul(-1);
    var priceImpactDeltaUsd = BigNumber.from(0);
    if (useMaxPriceImpact) {
        priceImpactDeltaUsd = maxNegativePriceImpactUsd;
    }
    else {
        priceImpactDeltaUsd = getPriceImpactForPosition(marketInfo, sizeInUsd.mul(-1), isLong, { fallbackToZero: true });
        if (priceImpactDeltaUsd.lt(maxNegativePriceImpactUsd)) {
            priceImpactDeltaUsd = maxNegativePriceImpactUsd;
        }
        // Ignore positive price impact
        if (priceImpactDeltaUsd.gt(0)) {
            priceImpactDeltaUsd = BigNumber.from(0);
        }
    }
    var liquidationCollateralUsd = applyFactor(sizeInUsd, marketInfo.minCollateralFactor);
    if (liquidationCollateralUsd.lt(minCollateralUsd)) {
        liquidationCollateralUsd = minCollateralUsd;
    }
    var liquidationPrice;
    if (getIsEquivalentTokens(collateralToken, indexToken)) {
        if (isLong) {
            var denominator = sizeInTokens.add(collateralAmount);
            if (denominator.eq(0)) {
                return undefined;
            }
            liquidationPrice = sizeInUsd
                .add(liquidationCollateralUsd)
                .sub(priceImpactDeltaUsd)
                .add(totalFeesUsd)
                .div(denominator)
                .mul(expandDecimals(1, indexToken.decimals));
        }
        else {
            var denominator = sizeInTokens.sub(collateralAmount);
            if (denominator.eq(0)) {
                return undefined;
            }
            liquidationPrice = sizeInUsd
                .sub(liquidationCollateralUsd)
                .add(priceImpactDeltaUsd)
                .sub(totalFeesUsd)
                .div(denominator)
                .mul(expandDecimals(1, indexToken.decimals));
        }
    }
    else {
        if (sizeInTokens.eq(0)) {
            return undefined;
        }
        var remainingCollateralUsd = collateralUsd.add(priceImpactDeltaUsd).sub(totalPendingFeesUsd).sub(closingFeeUsd);
        if (isLong) {
            liquidationPrice = liquidationCollateralUsd
                .sub(remainingCollateralUsd)
                .add(sizeInUsd)
                .div(sizeInTokens)
                .mul(expandDecimals(1, indexToken.decimals));
        }
        else {
            liquidationPrice = liquidationCollateralUsd
                .sub(remainingCollateralUsd)
                .sub(sizeInUsd)
                .div(sizeInTokens.mul(-1))
                .mul(expandDecimals(1, indexToken.decimals));
        }
    }
    if (liquidationPrice.lte(0)) {
        return undefined;
    }
    return liquidationPrice;
}
export function getPositionNetValue(p) {
    var pnl = p.pnl, closingFeeUsd = p.closingFeeUsd, collateralUsd = p.collateralUsd;
    var pendingFeesUsd = getPositionPendingFeesUsd(p);
    return collateralUsd.sub(pendingFeesUsd).sub(closingFeeUsd).add(pnl);
}
export function getEntryPrice(p) {
    var sizeInUsd = p.sizeInUsd, sizeInTokens = p.sizeInTokens, indexToken = p.indexToken;
    if (!sizeInTokens.gt(0)) {
        return undefined;
    }
    return sizeInUsd.div(sizeInTokens).mul(expandDecimals(1, indexToken.decimals));
}
export function isMarketOrderType(orderType) {
    return [OrderType.MarketDecrease, OrderType.MarketIncrease, OrderType.MarketSwap].includes(orderType);
}
export function applySlippageToPrice(allowedSlippage, price, isIncrease, isLong) {
    var shouldIncreasePrice = getShouldUseMaxPrice(isIncrease, isLong);
    var slippageBasisPoints = shouldIncreasePrice
        ? BASIS_POINTS_DIVISOR + allowedSlippage
        : BASIS_POINTS_DIVISOR - allowedSlippage;
    return price.mul(slippageBasisPoints).div(BASIS_POINTS_DIVISOR);
}
export function isVisibleOrder(orderType) {
    return isLimitOrderType(orderType) || isTriggerDecreaseOrderType(orderType) || isLimitSwapOrderType(orderType);
}
export function isLimitOrderType(orderType) {
    return [OrderType.LimitIncrease, OrderType.LimitSwap].includes(orderType);
}
export function isTriggerDecreaseOrderType(orderType) {
    return [OrderType.LimitDecrease, OrderType.StopLossDecrease].includes(orderType);
}
export function isDecreaseOrderType(orderType) {
    return [OrderType.MarketDecrease, OrderType.LimitDecrease, OrderType.StopLossDecrease].includes(orderType);
}
export function isIncreaseOrderType(orderType) {
    return [OrderType.MarketIncrease, OrderType.LimitIncrease].includes(orderType);
}
export function isSwapOrderType(orderType) {
    return [OrderType.MarketSwap, OrderType.LimitSwap].includes(orderType);
}
export function isLimitSwapOrderType(orderType) {
    return orderType === OrderType.LimitSwap;
}
export function isLiquidationOrderType(orderType) {
    return orderType === OrderType.Liquidation;
}
export function getOrderInfo(marketsInfoData, tokensData, wrappedNativeToken, order) {
    if (isSwapOrderType(order.orderType)) {
        var initialCollateralToken = getByKey(tokensData, order.initialCollateralTokenAddress);
        var outTokenAddress = getSwapPathOutputAddresses({
            marketsInfoData: marketsInfoData,
            swapPath: order.swapPath,
            initialCollateralAddress: order.initialCollateralTokenAddress,
            wrappedNativeTokenAddress: wrappedNativeToken.value,
            shouldUnwrapNativeToken: order.shouldUnwrapNativeToken,
        }).outTokenAddress;
        var targetCollateralToken = getByKey(tokensData, outTokenAddress);
        if (!initialCollateralToken || !targetCollateralToken) {
            return undefined;
        }
        var swapPathStats = getSwapPathStats({
            marketsInfoData: marketsInfoData,
            swapPath: order.swapPath,
            initialCollateralAddress: order.initialCollateralTokenAddress,
            wrappedNativeTokenAddress: wrappedNativeToken.value,
            usdIn: convertToUsd(order.initialCollateralDeltaAmount, initialCollateralToken.decimals, initialCollateralToken.prices.minPrice),
            shouldUnwrapNativeToken: order.shouldUnwrapNativeToken,
            shouldApplyPriceImpact: true,
        });
        var priceImpactAmount = convertToTokenAmount(swapPathStats === null || swapPathStats === void 0 ? void 0 : swapPathStats.totalSwapPriceImpactDeltaUsd, targetCollateralToken.decimals, targetCollateralToken.prices.minPrice);
        var swapFeeAmount = convertToTokenAmount(swapPathStats === null || swapPathStats === void 0 ? void 0 : swapPathStats.totalSwapFeeUsd, targetCollateralToken.decimals, targetCollateralToken.prices.minPrice);
        var toAmount = order.minOutputAmount.sub(priceImpactAmount || 0).add(swapFeeAmount || 0);
        var triggerRatio = getTokensRatioByAmounts({
            fromToken: initialCollateralToken,
            toToken: targetCollateralToken,
            fromTokenAmount: order.initialCollateralDeltaAmount,
            toTokenAmount: toAmount,
        });
        var title = getSwapOrderTitle({
            initialCollateralToken: initialCollateralToken,
            targetCollateralToken: targetCollateralToken,
            minOutputAmount: order.minOutputAmount,
            initialCollateralAmount: order.initialCollateralDeltaAmount,
        });
        var orderInfo = __assign(__assign({}, order), { swapPathStats: swapPathStats, triggerRatio: triggerRatio, title: title, initialCollateralToken: initialCollateralToken, targetCollateralToken: targetCollateralToken });
        return orderInfo;
    }
    else {
        var marketInfo = getByKey(marketsInfoData, order.marketAddress);
        var indexToken = marketInfo === null || marketInfo === void 0 ? void 0 : marketInfo.indexToken;
        var initialCollateralToken = getByKey(tokensData, order.initialCollateralTokenAddress);
        var outTokenAddress = getSwapPathOutputAddresses({
            marketsInfoData: marketsInfoData,
            swapPath: order.swapPath,
            initialCollateralAddress: order.initialCollateralTokenAddress,
            wrappedNativeTokenAddress: wrappedNativeToken.value,
            shouldUnwrapNativeToken: order.shouldUnwrapNativeToken,
        }).outTokenAddress;
        var targetCollateralToken = getByKey(tokensData, outTokenAddress);
        if (!marketInfo || !indexToken || !initialCollateralToken || !targetCollateralToken) {
            return undefined;
        }
        var title = getPositionOrderTitle({
            orderType: order.orderType,
            isLong: order.isLong,
            indexToken: indexToken,
            sizeDeltaUsd: order.sizeDeltaUsd,
        });
        var acceptablePrice = parseContractPrice(order.contractAcceptablePrice, indexToken.decimals);
        var triggerPrice = parseContractPrice(order.contractTriggerPrice, indexToken.decimals);
        var swapPathStats = getSwapPathStats({
            marketsInfoData: marketsInfoData,
            swapPath: order.swapPath,
            initialCollateralAddress: order.initialCollateralTokenAddress,
            wrappedNativeTokenAddress: wrappedNativeToken.value,
            usdIn: convertToUsd(order.initialCollateralDeltaAmount, initialCollateralToken.decimals, initialCollateralToken.prices.minPrice),
            shouldUnwrapNativeToken: order.shouldUnwrapNativeToken,
            shouldApplyPriceImpact: true,
        });
        var triggerThresholdType = getTriggerThresholdType(order.orderType, order.isLong);
        var orderInfo = __assign(__assign({}, order), { title: title, swapPathStats: swapPathStats, marketInfo: marketInfo, indexToken: indexToken, initialCollateralToken: initialCollateralToken, targetCollateralToken: targetCollateralToken, acceptablePrice: acceptablePrice, triggerPrice: triggerPrice, triggerThresholdType: triggerThresholdType });
        return orderInfo;
    }
}
export function getSwapOrderTitle(p) {
    var initialCollateralToken = p.initialCollateralToken, initialCollateralAmount = p.initialCollateralAmount, targetCollateralToken = p.targetCollateralToken, minOutputAmount = p.minOutputAmount;
    var fromTokenText = formatTokenAmount(initialCollateralAmount, initialCollateralToken.decimals, initialCollateralToken.symbol);
    var toTokenText = formatTokenAmount(minOutputAmount, targetCollateralToken.decimals, targetCollateralToken.symbol);
    return "Swap ".concat(fromTokenText, " for ").concat(toTokenText);
}
export function formatTokenAmount(amount, tokenDecimals, symbol, opts) {
    if (opts === void 0) { opts = {}; }
    var _a = opts.displayDecimals, displayDecimals = _a === void 0 ? 4 : _a, _b = opts.showAllSignificant, showAllSignificant = _b === void 0 ? false : _b, _c = opts.fallbackToZero, fallbackToZero = _c === void 0 ? false : _c, _d = opts.useCommas, useCommas = _d === void 0 ? false : _d, _e = opts.minThreshold, minThreshold = _e === void 0 ? '0' : _e, maxThreshold = opts.maxThreshold;
    var symbolStr = symbol ? "\u00A0".concat(symbol) : '';
    if (!amount || !tokenDecimals) {
        if (fallbackToZero) {
            amount = BigNumber.from(0);
            tokenDecimals = displayDecimals;
        }
        else {
            return undefined;
        }
    }
    var amountStr;
    if (showAllSignificant) {
        amountStr = formatAmountFree(amount, tokenDecimals, tokenDecimals);
    }
    else {
        var exceedingInfo = getLimitedDisplay(amount, tokenDecimals, { maxThreshold: maxThreshold, minThreshold: minThreshold });
        var symbol_1 = exceedingInfo.symbol ? "".concat(exceedingInfo.symbol, " ") : '';
        amountStr = "".concat(symbol_1).concat(formatAmount(exceedingInfo.value, tokenDecimals, displayDecimals, useCommas));
    }
    return "".concat(amountStr).concat(symbolStr);
}
export var formatAmountFree = function (amount, tokenDecimals, displayDecimals) {
    if (!amount) {
        return '...';
    }
    var amountStr = ethers.utils.formatUnits(amount, tokenDecimals);
    amountStr = limitDecimals(amountStr, displayDecimals);
    return trimZeroDecimals(amountStr);
};
export var trimZeroDecimals = function (amount) {
    if (parseFloat(amount) === parseInt(amount)) {
        return parseInt(amount).toString();
    }
    return amount;
};
export function parseContractPrice(price, tokenDecimals) {
    return price.mul(expandDecimals(1, tokenDecimals));
}
export function getSwapPathOutputAddresses(p) {
    var e_10, _a;
    var marketsInfoData = p.marketsInfoData, initialCollateralAddress = p.initialCollateralAddress, swapPath = p.swapPath, wrappedNativeTokenAddress = p.wrappedNativeTokenAddress, shouldUnwrapNativeToken = p.shouldUnwrapNativeToken;
    if (swapPath.length === 0) {
        return {
            outTokenAddress: shouldUnwrapNativeToken && initialCollateralAddress === wrappedNativeTokenAddress
                ? NATIVE_TOKEN_ADDRESS
                : initialCollateralAddress,
            outMarketAddress: undefined,
        };
    }
    var _b = __read(swapPath), firstMarketAddress = _b[0], marketAddresses = _b.slice(1);
    var outMarket = getByKey(marketsInfoData, firstMarketAddress);
    if (!outMarket) {
        return {
            outTokenAddress: undefined,
            outMarketAddress: undefined,
        };
    }
    var outTokenType = getTokenPoolType(outMarket, initialCollateralAddress);
    var outToken = outTokenType === 'long' ? outMarket.shortToken : outMarket.longToken;
    try {
        for (var marketAddresses_1 = __values(marketAddresses), marketAddresses_1_1 = marketAddresses_1.next(); !marketAddresses_1_1.done; marketAddresses_1_1 = marketAddresses_1.next()) {
            var marketAddress = marketAddresses_1_1.value;
            outMarket = getByKey(marketsInfoData, marketAddress);
            if (!outMarket) {
                return {
                    outTokenAddress: undefined,
                    outMarketAddress: undefined,
                };
            }
            outTokenType = outMarket.longTokenAddress === outToken.value ? 'short' : 'long';
            outToken = outTokenType === 'long' ? outMarket.longToken : outMarket.shortToken;
        }
    }
    catch (e_10_1) { e_10 = { error: e_10_1 }; }
    finally {
        try {
            if (marketAddresses_1_1 && !marketAddresses_1_1.done && (_a = marketAddresses_1.return)) _a.call(marketAddresses_1);
        }
        finally { if (e_10) throw e_10.error; }
    }
    var outTokenAddress = shouldUnwrapNativeToken && outToken.value === wrappedNativeTokenAddress ? NATIVE_TOKEN_ADDRESS : outToken.value;
    return {
        outTokenAddress: outTokenAddress,
        outMarketAddress: outMarket.marketTokenAddress,
    };
}
export function getTokensRatioByAmounts(p) {
    var fromToken = p.fromToken, toToken = p.toToken, fromTokenAmount = p.fromTokenAmount, toTokenAmount = p.toTokenAmount;
    var adjustedFromAmount = fromTokenAmount.mul(PRECISION).div(expandDecimals(1, fromToken.decimals));
    var adjustedToAmount = toTokenAmount.mul(PRECISION).div(expandDecimals(1, toToken.decimals));
    var _a = __read(adjustedFromAmount.gt(adjustedToAmount)
        ? [fromToken, toToken, adjustedFromAmount, adjustedToAmount]
        : [toToken, fromToken, adjustedToAmount, adjustedFromAmount], 4), smallestToken = _a[0], largestToken = _a[1], largestAmount = _a[2], smallestAmount = _a[3];
    var ratio = smallestAmount.gt(0) ? largestAmount.mul(PRECISION).div(smallestAmount) : BigNumber.from(0);
    return { ratio: ratio, largestToken: largestToken, smallestToken: smallestToken };
}
export function getPositionOrderTitle(p) {
    var orderType = p.orderType, isLong = p.isLong, indexToken = p.indexToken, sizeDeltaUsd = p.sizeDeltaUsd;
    var longShortText = isLong ? "Long" : "Short";
    var tokenText = "".concat(indexToken.symbol, " ").concat(longShortText);
    var sizeText = formatUsd(sizeDeltaUsd);
    var increaseOrDecreaseText = isIncreaseOrderType(orderType) ? "Increase" : "Decrease";
    return "".concat(increaseOrDecreaseText, " ").concat(tokenText, " by ").concat(sizeText);
}
export function formatUsd(usd, opts) {
    if (opts === void 0) { opts = {}; }
    var _a = opts.fallbackToZero, fallbackToZero = _a === void 0 ? false : _a, _b = opts.displayDecimals, displayDecimals = _b === void 0 ? 2 : _b;
    if (!usd) {
        if (fallbackToZero) {
            usd = BigNumber.from(0);
        }
        else {
            return undefined;
        }
    }
    var exceedingInfo = getLimitedDisplay(usd, USD_DECIMALS, opts);
    var sign = usd.lt(0) ? '-' : '';
    var displayUsd = formatAmount(exceedingInfo.value, USD_DECIMALS, displayDecimals, true);
    return "".concat(exceedingInfo.symbol).concat(sign, "$").concat(displayUsd);
}
export var formatAmount = function (amount, tokenDecimals, displayDecimals, useCommas, defaultValue) {
    if (!defaultValue) {
        defaultValue = '...';
    }
    if (amount === undefined || amount.toString().length === 0) {
        return defaultValue;
    }
    if (displayDecimals === undefined) {
        displayDecimals = 4;
    }
    var amountStr = ethers.utils.formatUnits(amount, tokenDecimals);
    amountStr = limitDecimals(amountStr, displayDecimals);
    if (displayDecimals !== 0) {
        amountStr = padDecimals(amountStr, displayDecimals);
    }
    if (useCommas) {
        return numberWithCommas(amountStr);
    }
    return amountStr;
};
export var limitDecimals = function (amount, maxDecimals) {
    var amountStr = amount.toString();
    if (maxDecimals === undefined) {
        return amountStr;
    }
    if (maxDecimals === 0) {
        return amountStr.split('.')[0];
    }
    var dotIndex = amountStr.indexOf('.');
    if (dotIndex !== -1) {
        var decimals = amountStr.length - dotIndex - 1;
        if (decimals > maxDecimals) {
            amountStr = amountStr.substr(0, amountStr.length - (decimals - maxDecimals));
        }
    }
    return amountStr;
};
export function numberWithCommas(x) {
    if (!x) {
        return '...';
    }
    var parts = x.toString().split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
}
export var padDecimals = function (amount, minDecimals) {
    var amountStr = amount.toString();
    var dotIndex = amountStr.indexOf('.');
    if (dotIndex !== -1) {
        var decimals = amountStr.length - dotIndex - 1;
        if (decimals < minDecimals) {
            amountStr = amountStr.padEnd(amountStr.length + (minDecimals - decimals), '0');
        }
    }
    else {
        amountStr = amountStr + '.0000';
    }
    return amountStr;
};
function getLimitedDisplay(amount, tokenDecimals, opts) {
    if (opts === void 0) { opts = {}; }
    var _a = opts.maxThreshold, maxThreshold = _a === void 0 ? MAX_EXCEEDING_THRESHOLD : _a, _b = opts.minThreshold, minThreshold = _b === void 0 ? MIN_EXCEEDING_THRESHOLD : _b;
    var max = expandDecimals(maxThreshold, tokenDecimals);
    var min = ethers.utils.parseUnits(minThreshold, tokenDecimals);
    var absAmount = amount.abs();
    if (absAmount.eq(0)) {
        return {
            symbol: '',
            value: absAmount,
        };
    }
    var symbol = absAmount.gt(max) ? TRIGGER_PREFIX_ABOVE : absAmount.lt(min) ? TRIGGER_PREFIX_BELOW : '';
    var value = absAmount.gt(max) ? max : absAmount.lt(min) ? min : absAmount;
    return {
        symbol: symbol,
        value: value,
    };
}
export function getTriggerDecreaseOrderType(p) {
    var triggerPrice = p.triggerPrice, markPrice = p.markPrice, isLong = p.isLong;
    var isTriggerAboveMarkPrice = triggerPrice.gt(markPrice);
    if (isTriggerAboveMarkPrice) {
        return isLong ? OrderType.LimitDecrease : OrderType.StopLossDecrease;
    }
    else {
        return isLong ? OrderType.StopLossDecrease : OrderType.LimitDecrease;
    }
}
export var applyAcceptablePrice = function (p) {
    var marketInfo = p.marketInfo, isLong = p.isLong, values = p.values, isTrigger = p.isTrigger, savedAcceptablePriceImpactBps = p.savedAcceptablePriceImpactBps;
    var acceptablePriceInfo = getAcceptablePriceInfo({
        marketInfo: marketInfo,
        isIncrease: false,
        isLong: isLong,
        indexPrice: values.indexPrice,
        sizeDeltaUsd: values.sizeDeltaUsd,
    });
    values.positionPriceImpactDeltaUsd = acceptablePriceInfo.priceImpactDeltaUsd;
    values.acceptablePrice = acceptablePriceInfo.acceptablePrice;
    values.acceptablePriceDeltaBps = acceptablePriceInfo.acceptablePriceDeltaBps;
    values.priceImpactDiffUsd = acceptablePriceInfo.priceImpactDiffUsd;
    if (isTrigger) {
        if (values.triggerOrderType === OrderType.StopLossDecrease) {
            if (isLong) {
                values.acceptablePrice = BigNumber.from(0);
            }
            else {
                values.acceptablePrice = ethers.constants.MaxUint256;
            }
        }
        else {
            var triggerAcceptablePriceInfo = getAcceptablePriceInfo({
                marketInfo: marketInfo,
                isIncrease: false,
                isLong: isLong,
                indexPrice: values.indexPrice,
                sizeDeltaUsd: values.sizeDeltaUsd,
                maxNegativePriceImpactBps: savedAcceptablePriceImpactBps,
            });
            values.acceptablePrice = triggerAcceptablePriceInfo.acceptablePrice;
            values.acceptablePriceDeltaBps = triggerAcceptablePriceInfo.acceptablePriceDeltaBps;
            values.priceImpactDiffUsd = triggerAcceptablePriceInfo.priceImpactDiffUsd;
            if (values.positionPriceImpactDeltaUsd.lt(triggerAcceptablePriceInfo.priceImpactDeltaUsd)) {
                values.positionPriceImpactDeltaUsd = triggerAcceptablePriceInfo.priceImpactDeltaUsd;
            }
        }
    }
    return values;
};
export function getIsFullClose(p) {
    var position = p.position, sizeDeltaUsd = p.sizeDeltaUsd, indexPrice = p.indexPrice, remainingCollateralUsd = p.remainingCollateralUsd, minCollateralUsd = p.minCollateralUsd, minPositionSizeUsd = p.minPositionSizeUsd;
    var marketInfo = position.marketInfo, isLong = position.isLong;
    if (position.sizeInUsd.sub(sizeDeltaUsd).lt(DUST_USD)) {
        return true;
    }
    var estimatedPnl = getPositionPnlUsd({
        marketInfo: marketInfo,
        sizeInUsd: position.sizeInUsd,
        sizeInTokens: position.sizeInTokens,
        markPrice: indexPrice,
        isLong: isLong,
    });
    var realizedPnl = estimatedPnl.mul(sizeDeltaUsd).div(position.sizeInUsd);
    var estimatedRemainingPnl = estimatedPnl.sub(realizedPnl);
    if (realizedPnl.lt(0)) {
        var estimatedRemainingCollateralUsd = remainingCollateralUsd.sub(realizedPnl);
        var minCollateralFactor = isLong
            ? marketInfo.minCollateralFactorForOpenInterestLong
            : marketInfo.minCollateralFactorForOpenInterestShort;
        var minCollateralFactorForMarket = marketInfo.minCollateralFactor;
        if (minCollateralFactorForMarket.gt(minCollateralFactor)) {
            minCollateralFactor = minCollateralFactorForMarket;
        }
        var minCollateralUsdForLeverage = applyFactor(position.sizeInUsd, minCollateralFactor);
        var willCollateralBeSufficient = estimatedRemainingCollateralUsd.gte(minCollateralUsdForLeverage);
        if (!willCollateralBeSufficient) {
            if (estimatedRemainingCollateralUsd.add(estimatedRemainingPnl).lt(minCollateralUsd) ||
                position.sizeInUsd.sub(sizeDeltaUsd).lt(minPositionSizeUsd)) {
                return true;
            }
        }
    }
    return false;
}
export function roundUpDivision(a, b) {
    return a.add(b).sub(1).div(b);
}
export function estimateCollateralCost(baseUsd, collateralToken, collateralPrice) {
    var amount = convertToTokenAmount(baseUsd, collateralToken.decimals, collateralToken.prices.minPrice);
    var usd = convertToUsd(amount, collateralToken.decimals, collateralPrice);
    return {
        amount: amount,
        usd: usd,
    };
}
export function payForCollateralCost(p) {
    var initialCostUsd = p.initialCostUsd, collateralToken = p.collateralToken, collateralPrice = p.collateralPrice, outputAmount = p.outputAmount, remainingCollateralAmount = p.remainingCollateralAmount;
    var values = {
        outputAmount: BigNumber.from(outputAmount),
        remainingCollateralAmount: BigNumber.from(remainingCollateralAmount),
        paidOutputAmount: BigNumber.from(0),
        paidRemainingCollateralAmount: BigNumber.from(0),
    };
    var remainingCostAmount = convertToTokenAmount(initialCostUsd, collateralToken.decimals, collateralPrice);
    if (remainingCostAmount.eq(0)) {
        return values;
    }
    if (values.outputAmount.gt(0)) {
        if (values.outputAmount.gt(remainingCostAmount)) {
            values.outputAmount = values.outputAmount.sub(remainingCostAmount);
            values.paidOutputAmount = remainingCostAmount;
            remainingCostAmount = BigNumber.from(0);
        }
        else {
            remainingCostAmount = remainingCostAmount.sub(values.outputAmount);
            values.paidOutputAmount = values.outputAmount;
            values.outputAmount = BigNumber.from(0);
        }
    }
    if (remainingCostAmount.eq(0)) {
        return values;
    }
    if (values.remainingCollateralAmount.gt(remainingCostAmount)) {
        values.remainingCollateralAmount = values.remainingCollateralAmount.sub(remainingCostAmount);
        values.paidRemainingCollateralAmount = remainingCostAmount;
        remainingCostAmount = BigNumber.from(0);
    }
    else {
        remainingCostAmount = remainingCostAmount.sub(remainingCollateralAmount);
        values.paidRemainingCollateralAmount = values.remainingCollateralAmount;
        values.remainingCollateralAmount = BigNumber.from(0);
    }
    return values;
}
export var parseValue = function (value, tokenDecimals) {
    var pValue = parseFloat(value);
    if (isNaN(pValue)) {
        return undefined;
    }
    value = limitDecimals(value, tokenDecimals);
    var amount = ethers.utils.parseUnits(value, tokenDecimals);
    return bigNumberify(amount);
};
export function estimateExecuteDecreaseOrderGasLimit(gasLimits, order) {
    var _a;
    var swapsCount = ((_a = order.swapPath) === null || _a === void 0 ? void 0 : _a.length) || 0;
    return gasLimits.decreaseOrder.add(gasLimits.singleSwap.mul(swapsCount)).add(order.callbackGasLimit || 0);
}
export function applySlippageToMinOut(allowedSlippage, minOutputAmount) {
    var slippageBasisPoints = BASIS_POINTS_DIVISOR - allowedSlippage;
    return minOutputAmount.mul(slippageBasisPoints).div(BASIS_POINTS_DIVISOR);
}
export function isMarketIndexToken(marketInfo, tokenAddress) {
    return (tokenAddress === marketInfo.indexToken.value ||
        (tokenAddress === NATIVE_TOKEN_ADDRESS && marketInfo.indexToken.isWrapped));
}
